import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import useAuthStore from '../context/authStore';
import {
  getMyEnrollments, getTeacherEnrollments, getEarnings,
  createCourse, updateCourse, updateMe, uploadVideo, getMyCourses,
  uploadBanner, uploadSubjectImage, uploadThumbnail,
  uploadPdf, getCoursePdfs, deletePdf,
  createQuiz, updateQuiz, getQuiz, getLessonQuiz,
  getAnnouncements, getMyAnnouncements, createAnnouncement, deleteAnnouncement,
  getTeacherAnalytics, getStudentAnalytics, getPublicStats,
} from '../services/api';
import api from '../services/api';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollReveal } from '../hooks/useScrollReveal';
import AvatarUpload from '../components/AvatarUpload';
import BannerUpload from '../components/BannerUpload';
import SubjectSelector from '../components/SubjectSelector';
import CourseCard from '../components/CourseCard';
import { Skeleton, CourseCardSkeleton } from '../components/Skeleton';
import Certificate from '../components/Certificate';
import './DashboardPage.css';

/* ─── Egyptian grades ─── */
export const GRADES = [
  'KG1','KG2',
  'الصف الأول الابتدائي','الصف الثاني الابتدائي','الصف الثالث الابتدائي',
  'الصف الرابع الابتدائي','الصف الخامس الابتدائي','الصف السادس الابتدائي',
  'الصف الأول الإعدادي','الصف الثاني الإعدادي','الصف الثالث الإعدادي',
  'الصف الأول الثانوي','الصف الثاني الثانوي','الصف الثالث الثانوي',
  'ثانوي صناعي','ثانوي تجاري','ثانوي زراعي','ثانوي فندقي',
  'السنة الأولى جامعة','السنة الثانية جامعة','السنة الثالثة جامعة','السنة الرابعة جامعة','خريج / عام'
];

/* ─── Upload course thumbnail ─── */
async function uploadCourseThumbnail(file) {
  const fd = new FormData();
  fd.append('image', file);
  const { data } = await uploadThumbnail(fd);
  return data.url;
}

/* ─── Upload course banner ─── */
async function uploadCourseBannerFile(file, courseId) {
  const fd = new FormData();
  fd.append('banner', file);
  fd.append('courseId', courseId);
  const { data } = await api.post('/upload/course-banner', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.url;
}

/* ══════════════════ ROUTER ══════════════════ */
export default function DashboardPage() {
  const { userDoc, refreshUserDoc } = useAuthStore();
  if (userDoc?.role === 'teacher') return <TeacherDashboard userDoc={userDoc} refreshUserDoc={refreshUserDoc} />;
  if (userDoc?.role === 'admin')   return <AdminRedirect />;
  return <StudentDashboard userDoc={userDoc} refreshUserDoc={refreshUserDoc} />;
}

function AdminRedirect() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
      <div style={{ fontSize: 52 }}>🛡️</div>
      <h2 style={{ fontWeight: 800 }}>أنت أدمن</h2>
      <Link to="/admin" className="btn btn-primary btn-lg">الذهاب للوحة الأدمن</Link>
    </div>
  );
}

/* ─── Quiz Modal ─── */
function QuizModal({ lessonTitle, initialQuiz, onSave, onClose }) {
  const [questions, setQuestions] = useState(initialQuiz?.questions || []);
  const [quizTitle, setQuizTitle] = useState(initialQuiz?.title || `اختبار: ${lessonTitle}`);

  const addQuestion = () => {
    setQuestions([...questions, {
      id: Date.now().toString(),
      type: 'multiple_choice',
      questionText: '',
      options: ['', '', '', ''],
      correctAnswer: '',
      explanation: ''
    }]);
  };

  const updateQuestion = (id, key, val) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, [key]: val } : q));
  };

  const removeQuestion = (id) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const handleSave = () => {
    if (!quizTitle.trim()) return toast.error('يرجى إدخال عنوان للاختبار');
    if (questions.length === 0) return toast.error('يجب إضافة سؤال واحد على الأقل');

    // Validation
    for (const q of questions) {
      if (!q.questionText.trim()) return toast.error('يرجى إدخال نص السؤال');
      if (q.type === 'multiple_choice') {
        if (q.options.some(opt => !opt.trim())) return toast.error('يرجى إكمال جميع الخيارات');
        if (!q.correctAnswer) return toast.error('يرجى اختيار الإجابة الصحيحة');
      } else {
        if (!q.correctAnswer) return toast.error('يرجى اختيار (صح) أو (خطأ)');
      }
    }

    onSave({ title: quizTitle, questions });
  };

  return (
    <div className="lp-modal-overlay" style={{ zIndex: 2000 }}>
      <div className="lp-modal" style={{ maxWidth: 720, width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="lp-modal-top">
          <h3 style={{ margin: 0 }}>📝 إدارة الاختبار: {lessonTitle}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div className="form-group" style={{ marginTop: 20 }}>
          <label className="form-label">عنوان الاختبار</label>
          <input className="form-input" value={quizTitle} onChange={e => setQuizTitle(e.target.value)} />
        </div>

        <div className="quiz-questions-list" style={{ marginTop: 20 }}>
          {questions.map((q, idx) => (
            <div key={q.id} className="quiz-question-card" style={{
              padding: 16, marginBottom: 16, background: '#f8fafc',
              border: '1px solid var(--border)', borderRadius: 8
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <strong style={{ color: 'var(--blue)' }}>السؤال {idx + 1}</strong>
                <button className="btn btn-ghost btn-xs" style={{ color: 'var(--danger)' }} onClick={() => removeQuestion(q.id)}>حذف 🗑</button>
              </div>

              <div className="grid-2" style={{ gap: 15 }}>
                <div className="form-group">
                  <label className="form-label">نوع السؤال</label>
                  <select className="form-input" value={q.type} onChange={e => {
                    const type = e.target.value;
                    updateQuestion(q.id, 'type', type);
                    if (type === 'true_false') updateQuestion(q.id, 'options', []);
                    else updateQuestion(q.id, 'options', ['', '', '', '']);
                    updateQuestion(q.id, 'correctAnswer', '');
                  }}>
                    <option value="multiple_choice">اختيار من متعدد</option>
                    <option value="true_false">صح أو خطأ</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">نص السؤال</label>
                <textarea className="form-input" rows={2} value={q.questionText} onChange={e => updateQuestion(q.id, 'questionText', e.target.value)} />
              </div>

              {q.type === 'multiple_choice' ? (
                <div className="quiz-options-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {q.options.map((opt, oi) => (
                    <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="radio" name={`correct-${q.id}`} checked={q.correctAnswer === opt && opt !== ''}
                        onChange={() => updateQuestion(q.id, 'correctAnswer', opt)} />
                      <input className="form-input" placeholder={`خيار ${oi + 1}`} value={opt}
                        onChange={e => {
                          const newOpts = [...q.options];
                          newOpts[oi] = e.target.value;
                          updateQuestion(q.id, 'options', newOpts);
                          if (q.correctAnswer === opt) updateQuestion(q.id, 'correctAnswer', e.target.value);
                        }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 20, padding: '10px 0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="radio" name={`correct-${q.id}`} checked={q.correctAnswer === 'صح'} onChange={() => updateQuestion(q.id, 'correctAnswer', 'صح')} /> صح
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="radio" name={`correct-${q.id}`} checked={q.correctAnswer === 'خطأ'} onChange={() => updateQuestion(q.id, 'correctAnswer', 'خطأ')} /> خطأ
                  </label>
                </div>
              )}

              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">شرح الإجابة (اختياري)</label>
                <input className="form-input" placeholder="يظهر للطالب بعد الإجابة..." value={q.explanation} onChange={e => updateQuestion(q.id, 'explanation', e.target.value)} />
              </div>
            </div>
          ))}
        </div>

        <button className="btn btn-outline" style={{ width: '100%', marginBottom: 20, borderStyle: 'dashed' }} onClick={addQuestion}>+ إضافة سؤال جديد</button>

        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <button className="btn btn-primary" style={{ flex: 1, padding: 12 }} onClick={handleSave}>💾 حفظ الاختبار</button>
          <button className="btn btn-outline" style={{ flex: 1, padding: 12 }} onClick={onClose}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Referral Card Component ─── */
function ReferralCard({ userDoc, isTeacher = false }) {
  return (
    <div className="card referral-card" style={{ padding: 20, marginTop: 20, background: 'linear-gradient(135deg, #1a9bd5 0%, #0dd4b8 100%)', color: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18 }}>{isTeacher ? '🤝 انشر المنصة!' : '🎁 شارك واكسب!'}</h3>
          <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
            {isTeacher
              ? 'ادعُ زملائك المدرسين للانضمام واكسب 10 ج.م في محفظتك.'
              : 'شارك رابط الإحالة الخاص بك واكسب 10 ج.م رصيد عن كل صديق.'}
          </p>
          <div style={{ marginTop: 8, fontSize: 12, background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: 20, width: 'fit-content' }}>
            📊 إجمالي الإحالات: <strong>{userDoc?.totalReferrals || 0}</strong>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: 'rgba(255,255,255,0.2)', padding: '8px 12px', borderRadius: 8 }}>
          <code style={{ fontSize: 14, fontWeight: 700 }}>{userDoc?.referralCode}</code>
          <button
            className="btn btn-sm"
            style={{ background: '#fff', color: '#1a9bd5', border: 'none', fontWeight: 800 }}
            onClick={() => {
              const link = `${window.location.origin}/register?ref=${userDoc?.referralCode}`;
              navigator.clipboard.writeText(link);
              toast.success('📋 تم نسخ رابط الإحالة!');
            }}
          >
            نسخ الرابط
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════ STUDENT DASHBOARD ══════════════════ */
function StudentDashboard({ userDoc, refreshUserDoc }) {
  const [tab, setTab]                 = useState('overview');
  const [enrollments, setEnrollments] = useState([]);
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [viewCert, setViewCert]       = useState(null);
  const pageRef = useScrollReveal();

  useEffect(() => {
    Promise.all([
      getMyEnrollments(),
      api.get('/quizzes/my-attempts').catch(() => ({ data: [] }))
    ])
      .then(([er, qr]) => {
        setEnrollments(er.data || []);
        setRecentAttempts(qr.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const completed  = enrollments.filter(e => e.progress === 100);
  const inProgress = enrollments.filter(e => e.progress > 0 && e.progress < 100);

  const activeEnrollment = [...enrollments]
    .filter(e => e.progress > 0 && e.progress < 100)
    .sort((a, b) => new Date(b.updatedAt || b.enrolledAt) - new Date(a.updatedAt || a.enrolledAt))[0];

  const TABS = [
    { key: 'overview', label: '🏠 الرئيسية' },
    { key: 'analytics', label: '📊 إحصائياتي' },
    { key: 'wallet', label: '💰 محفظتي' },
    { key: 'quizzes', label: '📝 الاختبارات' },
    { key: 'notifications', label: '🔔 التنبيهات' },
    { key: 'courses',  label: `📚 كورساتي (${enrollments.length})` },
    { key: 'wishlist', label: `❤️ الكورسات (${userDoc?.wishlist?.length || 0})` },
    { key: 'favorites', label: `👨‍🏫 المدرسون (${userDoc?.favoriteTeachers?.length || 0})` },
    { key: 'review',   label: '⭐ تقييم المنصة' },
    { key: 'profile',  label: '⚙️ ملفي الشخصي' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      className="dashboard"
      ref={pageRef}
    >
      <div className="db-header">
        <div className="db-header-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="avatar" style={{ width: 52, height: 52, fontSize: 20, flexShrink: 0 }}>
              {userDoc?.name?.[0] || '؟'}
            </div>
            <div>
              <h1>أهلاً، {userDoc?.name} 👋</h1>
              <p>طالب · عضو منذ {userDoc?.createdAt ? new Date(userDoc.createdAt).toLocaleDateString('ar-EG') : '—'}</p>
            </div>
          </div>
          <Link to="/courses" className="btn btn-outline">تصفح كورسات جديدة</Link>
        </div>
      </div>

      <div className="db-body">
        <div className="db-tabs">
          {TABS.map(t => (
            <button key={t.key} className={`db-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div>
            <div className="db-stats-row">
              <div className="db-stat-card"><span className="db-stat-num">{enrollments.length}</span><span className="db-stat-lbl">كورس مسجل</span></div>
              <div className="db-stat-card"><span className="db-stat-num">{inProgress.length}</span><span className="db-stat-lbl">قيد التعلم</span></div>
              <div className="db-stat-card"><span className="db-stat-num">{completed.length}</span><span className="db-stat-lbl">مكتمل</span></div>
              <div className="db-stat-card" style={{ border: '1px solid var(--teal)', background: 'rgba(13,212,184,0.05)' }}>
                <span className="db-stat-num" style={{ color: 'var(--teal)' }}>{userDoc?.credits || 0} <small style={{ fontSize: 12 }}>ج.م</small></span>
                <span className="db-stat-lbl">رصيد الهدايا</span>
              </div>
            </div>

            {/* Progress Psychology Banner */}
            {activeEnrollment && (
              <div className="card progress-banner" style={{ padding: 20, marginTop: 20, border: '2px solid var(--blue)', background: 'rgba(26,155,213,0.05)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'relative', zIndex: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 18, color: 'var(--blue)' }}>🚀 كمل طريقك!</h3>
                    <span className="badge badge-blue" style={{ fontSize: 12 }}>{activeEnrollment.courseTitle}</span>
                  </div>
                  <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 15 }}>
                    أنت خلصت <span style={{ color: 'var(--blue)', fontSize: 20 }}>{activeEnrollment.progress}%</span> من الكورس — فاضلك <span style={{ color: 'var(--teal)', fontSize: 20 }}>{(activeEnrollment.course?.lessons?.length || 0) - (activeEnrollment.completedLessons?.length || 0)}</span> دروس بس وتخلص! 🎓
                  </p>
                  <div className="progress-bar" style={{ height: 10, background: 'rgba(0,0,0,0.05)' }}>
                    <div className="progress-fill" style={{ width: `${activeEnrollment.progress}%`, background: 'linear-gradient(90deg, #1a9bd5, #0dd4b8)' }} />
                  </div>
                  <Link
                    to={`/courses/${activeEnrollment.courseSlug || activeEnrollment.course?.slug || activeEnrollment.courseId}`}
                    className="btn btn-primary btn-sm"
                    style={{ marginTop: 15, width: 'fit-content', boxShadow: '0 4px 12px rgba(26,155,213,0.3)' }}
                  >
                    متابعة التعلم الآن ←
                  </Link>
                </div>
                <div style={{ position: 'absolute', right: -20, bottom: -20, fontSize: 100, opacity: 0.05, transform: 'rotate(-15deg)' }}>🏆</div>
              </div>
            )}

            <ReferralCard userDoc={userDoc} />

            {enrollments.length > 0 && (
              <div className="card" style={{ padding: 20, marginTop: 20 }}>
                <h3 className="db-section-title">تابع تعلمك</h3>
                <div className="enrollments-list">
                  {enrollments.slice(0, 4).map((e, i) => (
                    <div key={i} className="enrollment-card card">
                      <div className="enroll-thumb">
                        {e.courseThumbnail
                          ? <img src={e.courseThumbnail} alt="" />
                          : <div className="enroll-thumb-ph">📚</div>
                        }
                      </div>
                      <div className="enroll-body">
                        <div className="enroll-title">{e.courseTitle || e.courseId}</div>
                        <div className="progress-bar" style={{ marginTop: 6 }}>
                          <div className="progress-fill" style={{ width: `${e.progress || 0}%` }} />
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{e.progress || 0}% مكتمل</div>
                      </div>
                      <div className="enroll-actions" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <Link to={`/courses/${e.courseSlug || e.course?.slug || e.courseId}`} className="btn btn-primary btn-sm">متابعة</Link>
                        {e.progress >= 100 && e.isCertificateEnabled && (
                          <button
                            className="btn btn-outline btn-sm"
                            style={{ fontSize: 10 }}
                            onClick={() => {
                              api.get(`/enrollments/certificate/${e.courseId}`)
                                .then(r => setViewCert(r.data))
                                  .catch((err) => {
                                    if (err.response?.status === 403 && err.response?.data?.missingQuizCount) {
                                      toast.error(`تحتاج لاجتياز ${err.response.data.missingQuizCount} اختبار متبقي أولاً 📝`);
                                    } else {
                                      toast.error(err.response?.data?.error || 'الشهادة قيد التجهيز، جرب لاحقاً');
                                    }
                                  });
                            }}
                          >
                            🎓 الشهادة
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {recentAttempts.length > 0 && (
              <div className="card" style={{ padding: 20, marginTop: 20 }}>
                <h3 className="db-section-title">آخر الاختبارات</h3>
                <div className="recent-quizzes-mini">
                  {recentAttempts.sort((a,b) => new Date(b.submittedAt) - new Date(a.submittedAt)).slice(0, 3).map((a, i) => (
                    <Link key={i} to={`/courses/${a.courseSlug || a.courseId}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < 2 ? '1px solid var(--border)' : 'none', textDecoration: 'none', color: 'inherit' }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{a.quizTitle || 'اختبار درس'}</div>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: a.score >= 60 ? 'var(--success)' : 'var(--danger)' }}>{a.score}%</span>
                        <span className={`badge ${a.score >= 60 ? 'badge-green' : 'badge-amber'}`} style={{ fontSize: 10 }}>{a.score >= 60 ? 'ناجح' : 'مراجعة'}</span>
                      </div>
                    </Link>
                  ))}
                </div>
                <button className="btn btn-ghost btn-sm" style={{ width: '100%', marginTop: 10 }} onClick={() => setTab('quizzes')}>عرض كل النتائج ←</button>
              </div>
            )}
          </div>
        )}

        {tab === 'courses' && (
          loading ? (
            <div className="enrollments-list">
              {Array(3).fill(0).map((_, i) => (
                <div key={i} className="enrollment-card card">
                  <Skeleton width="100px" height="60px" borderRadius="8px" />
                  <div className="enroll-body" style={{ flex: 1, padding: '0 16px' }}>
                    <Skeleton width="60%" height="18px" className="mb-8" />
                    <Skeleton width="100%" height="8px" borderRadius="4px" />
                  </div>
                  <Skeleton width="80px" height="32px" borderRadius="6px" />
                </div>
              ))}
            </div>
          ) : enrollments.length === 0 ? (
            <div className="db-empty">
              <div style={{ fontSize: 48 }}>📭</div>
              <h3>لم تسجل في أي كورس بعد</h3>
              <Link to="/courses" className="btn btn-primary">تصفح الكورسات</Link>
            </div>
          ) : (
            <div className="enrollments-list">
              {enrollments.map((e, i) => (
                <div key={i} className="enrollment-card card">
                  <div className="enroll-thumb">
                    {e.courseThumbnail ? <img src={e.courseThumbnail} alt="" /> : <div className="enroll-thumb-ph">📚</div>}
                  </div>
                  <div className="enroll-body">
                    <div className="enroll-title">{e.courseTitle || e.courseId}</div>
                    <div className="progress-bar" style={{ marginTop: 6 }}>
                      <div className="progress-fill" style={{ width: `${e.progress || 0}%` }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{e.progress || 0}% مكتمل · {e.completedLessons?.length || 0} درس</div>
                  </div>
                  <div className="enroll-actions" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Link to={`/courses/${e.courseSlug || e.course?.slug || e.courseId}`} className="btn btn-primary btn-sm">متابعة</Link>
                    {e.progress >= 100 && e.isCertificateEnabled && (
                      <button
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: 10 }}
                        onClick={() => {
                          api.get(`/enrollments/certificate/${e.courseId}`)
                            .then(r => setViewCert(r.data))
                            .catch((err) => {
                              if (err.response?.status === 403 && err.response?.data?.missingQuizCount) {
                                toast.error(`تحتاج لاجتياز ${err.response.data.missingQuizCount} اختبار متبقي أولاً 📝`);
                              } else {
                                toast.error(err.response?.data?.error || 'الشهادة قيد التجهيز، جرب لاحقاً');
                              }
                            });
                        }}
                      >
                        🎓 الشهادة
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'notifications' && (
          <StudentNotificationsTab />
        )}

        {tab === 'analytics' && (
          <StudentAnalyticsTab />
        )}

        {tab === 'wallet' && (
          <StudentWalletTab userDoc={userDoc} />
        )}

        {tab === 'quizzes' && (
          <StudentQuizHistory />
        )}

        {tab === 'wishlist' && (
          <StudentWishlistTab userDoc={userDoc} />
        )}

        {tab === 'favorites' && (
          <StudentFavoriteTeachersTab userDoc={userDoc} />
        )}

        {tab === 'review' && (
          <PlatformReviewTab />
        )}

        {tab === 'profile' && (
          <StudentProfileForm userDoc={userDoc} refreshUserDoc={refreshUserDoc} />
        )}
      </div>

      {viewCert && <Certificate certificate={viewCert} onClose={() => setViewCert(null)} />}

      {/* Certificate Premium Upsell Modal */}
      <AnimatePresence>
        {viewCert && !viewCert.isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="cert-upsell-modal"
            style={{
              position: 'fixed', bottom: 20, right: 20, zIndex: 6000,
              width: 320, padding: 20, background: '#fff', border: '2px solid #d4af37',
              borderRadius: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', direction: 'rtl'
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 10, fontFamily: 'Cairo' }}>✨ تَمَيَّز بشهادتك!</div>
            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.5, marginBottom: 15 }}>
              رقّي شهادتك للنسخة <strong>البريميوم</strong> بختم مذهب وتصميم فاخر يخلي ملفك الشخصي يلمع!
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 15 }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#d4af37' }}>49 ج.م</span>
              <span style={{ fontSize: 12, color: '#999', textDecoration: 'line-through' }}>99 ج.م</span>
            </div>
            <button
              className="btn btn-primary w-100"
              style={{ background: 'linear-gradient(135deg, #d4af37, #b8860b)', border: 'none' }}
              onClick={async () => {
                try {
                  const { data } = await api.post('/payments/checkout', {
                    courseId: viewCert.courseId,
                    purchaseType: 'certificate',
                    certificateId: viewCert.id,
                    method: 'card'
                  });
                  if (data.iframeUrl) window.location.href = data.iframeUrl;
                } catch { toast.error('فشل بدء عملية الدفع'); }
              }}
            >
              رقي للبريميوم الآن 🚀
            </button>
            <button
              className="btn btn-ghost btn-xs w-100"
              style={{ marginTop: 8, color: '#999' }}
              onClick={() => setViewCert({ ...viewCert, isPremium: 'dismissed' })}
            >
              لا شكراً، النسخة العادية تكفي
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ─── Student Wishlist Tab ─── */
function StudentWishlistTab({ userDoc }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const wishlist = userDoc?.wishlist || [];
    if (wishlist.length === 0) {
      setCourses([]);
      return;
    }
    setLoading(true);
    // Fetch courses in bulk
    api.get('/courses', { params: { ids: wishlist.join(',') } })
      .then(r => {
        setCourses(r.data.courses || []);
      })
      .catch(() => {
        setCourses([]);
      })
      .finally(() => setLoading(false));
  }, [userDoc?.wishlist]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div>
      <h2 className="db-section-title">المفضلة ({courses.length})</h2>
      {courses.length === 0 ? (
        <div className="db-empty">
          <div style={{ fontSize: 48 }}>💔</div>
          <h3>قائمة المفضلة فارغة</h3>
          <Link to="/courses" className="btn btn-primary">تصفح الكورسات لإضافتها</Link>
        </div>
      ) : (
        <div className="courses-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
          {courses.map(c => <CourseCard key={c.id} course={c} />)}
        </div>
      )}
    </div>
  );
}

/* ─── Student Wallet Tab ─── */
function StudentWalletTab({ userDoc }) {
  return (
    <div>
      <h2 className="db-section-title">محفظتي — رصيد الهدايا 💰</h2>
      <div className="grid-2">
        <div className="card" style={{ padding: 24, textAlign: 'center', border: '2px solid var(--teal)', background: 'rgba(13,212,184,0.05)' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🎁</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 5 }}>رصيدك الحالي</div>
          <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--teal)' }}>{userDoc?.credits || 0} ج.م</div>
          <p style={{ fontSize: 12, marginTop: 10, color: 'var(--text-muted)' }}>
            يمكنك استخدام هذا الرصيد للحصول على خصومات عند شراء الكورسات.
          </p>
        </div>

        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontSize: 16, marginBottom: 12 }}>كيف تزيد رصيدك؟ 🚀</h3>
          <ul style={{ paddingRight: 20, fontSize: 13, lineHeight: 1.8 }}>
            <li>شارك رابط الإحالة الخاص بك مع زملائك.</li>
            <li>عندما يسجل زميلك باستخدام الرابط، ستحصل على <strong>10 ج.م</strong> هدية.</li>
            <li>تابع العروض والمسابقات التي تنظمها المنصة.</li>
          </ul>
        </div>
      </div>

      <div className="card" style={{ padding: 20, marginTop: 20 }}>
        <h3 style={{ fontSize: 16, marginBottom: 15 }}>آخر العمليات</h3>
        <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px 0', fontSize: 13 }}>لا توجد عمليات سابقة حالياً.</p>
      </div>
    </div>
  );
}

/* ─── Student Favorite Teachers Tab ─── */
function StudentFavoriteTeachersTab({ userDoc }) {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const favorites = userDoc?.favoriteTeachers || [];
    if (favorites.length === 0) {
      setTeachers([]);
      return;
    }
    setLoading(true);
    Promise.all(favorites.map(id => api.get(`/users/teachers/${id}`).catch(() => null)))
      .then(responses => {
        setTeachers(responses.map(r => r?.data).filter(Boolean));
      })
      .finally(() => setLoading(false));
  }, [userDoc?.favoriteTeachers]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div>
      <h2 className="db-section-title">المدرسون المفضلون ({teachers.length})</h2>
      {teachers.length === 0 ? (
        <div className="db-empty">
          <div style={{ fontSize: 48 }}>👨‍🏫</div>
          <h3>لم تضف أي مدرس للمفضلة بعد</h3>
          <Link to="/teachers" className="btn btn-primary">تصفح المدرسين</Link>
        </div>
      ) : (
        <div className="teachers-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
          {teachers.map(t => (
            <Link key={t.uid} to={`/teachers/${t.username || t.uid}`} className="card" style={{ padding: 16, textAlign: 'center', textDecoration: 'none', color: 'inherit' }}>
              <div className="avatar" style={{ width: 64, height: 64, margin: '0 auto 12px' }}>
                {t.avatar ? <img src={t.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : t.name?.[0]}
              </div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{t.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.subjects?.slice(0, 2).join(' · ')}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Student Notifications Tab ─── */
function StudentNotificationsTab() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyAnnouncements()
      .then(r => setAnnouncements(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div>
      <h2 className="db-section-title">آخر التنبيهات من مدرسيك 🔔</h2>
      {announcements.length === 0 ? (
        <div className="db-empty">
          <div style={{ fontSize: 48 }}>🔕</div>
          <h3>لا توجد تنبيهات جديدة</h3>
          <p>التنبيهات من المدرسين في الكورسات المشترك فيها ستظهر هنا.</p>
        </div>
      ) : (
        <div className="announcements-list">
          {announcements.map(a => (
            <div key={a.id} className="announcement-item card" style={{ padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong style={{ color: 'var(--blue)' }}>{a.title}</strong>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(a.createdAt).toLocaleDateString('ar-EG')}</span>
              </div>
              <p style={{ fontSize: 13, whiteSpace: 'pre-wrap', marginBottom: 10 }}>{a.content}</p>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                بواسطة: <strong>{a.teacherName}</strong> · كورس: <Link to={`/courses/${a.courseSlug || a.courseId}`} style={{ color: 'inherit' }}>عرض الكورس ←</Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Student Analytics Tab ─── */
function StudentQuizHistory() {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/quizzes/my-attempts')
      .then(r => setAttempts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div>
      <h2 className="db-section-title">تاريخ الاختبارات 📝</h2>
      {attempts.length === 0 ? (
        <div className="db-empty">
          <div style={{ fontSize: 48 }}>📝</div>
          <h3>لم تؤدِ أي اختبارات بعد</h3>
          <p>ابدأ بمذاكرة دروسك وحل الاختبارات المرفقة بها.</p>
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="db-table">
            <thead>
              <tr>
                <th>الاختبار</th>
                <th>الدرجة</th>
                <th>النتيجة</th>
                <th>التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {attempts.sort((a,b) => new Date(b.submittedAt) - new Date(a.submittedAt)).map((a, i) => (
                <tr key={i}>
                  <td style={{ fontSize: 13 }}><Link to={`/courses/${a.courseSlug || a.courseId}`}>{a.quizTitle || 'اختبار درس'}</Link></td>
                  <td style={{ fontWeight: 800, color: a.score >= 60 ? 'var(--success)' : 'var(--danger)' }}>{a.score}%</td>
                  <td>
                    <span className={`badge ${a.score >= 60 ? 'badge-green' : 'badge-amber'}`}>
                      {a.score >= 60 ? 'ناجح ✅' : 'يحتاج مراجعة 📚'}
                    </span>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {new Date(a.submittedAt).toLocaleDateString('ar-EG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TeacherQuizManagement({ myCourses, setEditCourse }) {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/quizzes/teacher/all')
      .then(r => setQuizzes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div>
      <h2 className="db-section-title">إدارة الاختبارات 📝</h2>
      {quizzes.length === 0 ? (
        <div className="db-empty">
          <div style={{ fontSize: 48 }}>📝</div>
          <h3>لم تنشئ أي اختبارات بعد</h3>
          <p>يمكنك إضافة اختبارات لدروس الكورس من خلال صفحة تعديل الكورس.</p>
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="db-table">
            <thead>
              <tr>
                <th>عنوان الاختبار</th>
                <th>عدد الأسئلة</th>
                <th>آخر تحديث</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {quizzes.map((q, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>
                    <div>{q.title}</div>
                    {q.courseTitle && <div style={{ fontSize: 10, color: 'var(--blue)', marginTop: 2 }}>{q.courseTitle}</div>}
                  </td>
                  <td>{q.questions?.length || 0} سؤال</td>
                  <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {new Date(q.updatedAt || q.createdAt).toLocaleDateString('ar-EG')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Link to={`/courses/${q.courseSlug || q.courseId}`} className="btn btn-outline btn-xs">فتح الكورس</Link>
                      <button className="btn btn-primary btn-xs" onClick={() => {
                        // Find the course in myCourses and trigger its edit mode
                        const c = myCourses.find(x => x.id === q.courseId);
                        if (c) setEditCourse(c);
                        else toast.error('يرجى الانتقال لتبويب كورساتي لتعديل الكورس');
                      }}>✏️ تعديل الكورس</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StudentAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStudentAnalytics().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;
  if (!data) return <div className="db-empty"><h3>لا توجد بيانات كافية للتحليل</h3></div>;

  return (
    <div className="tab-content">
      <h2 className="db-section-title">تحليلات تعلمك 📈</h2>
      <div className="grid-2">
        {/* Progress per Course */}
        <div className="card" style={{ padding: 20 }}>
          <h3 className="db-section-title" style={{ fontSize: 16 }}>تقدمك في الكورسات (%)</h3>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.progressData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#1a9bd5" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Activity heat map / learning streak placeholder */}
        <div className="card" style={{ padding: 20 }}>
          <h3 className="db-section-title" style={{ fontSize: 16 }}>نشاط التعلم (تاريخ التحديث)</h3>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.activityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="value" stroke="#0dd4b8" fill="#0dd4b8" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function StudentProfileForm({ userDoc, refreshUserDoc }) {
  const { register, handleSubmit, formState: { isSubmitting } } = useForm({
    defaultValues: {
      name: userDoc?.name,
      bio: userDoc?.bio,
      phone: userDoc?.phone,
      parentPhone: userDoc?.parentPhone,
      grade: userDoc?.grade,
    },
  });
  const onSubmit = async (data) => {
    try {
      await updateMe({
        name: data.name,
        bio: data.bio,
        phone: data.phone,
        parentPhone: data.parentPhone,
        grade: data.grade,
      });
      await refreshUserDoc();
      toast.success('✅ تم التحديث!');
    }
    catch { toast.error('حدث خطأ'); }
  };
  return (
    <div className="create-course-wrap">
      <h2 className="db-section-title">الملف الشخصي</h2>
      <div className="card" style={{ padding: 24 }}>
        <div className="profile-avatar-row">
          <AvatarUpload />
          <div><div style={{ fontWeight: 700 }}>{userDoc?.name}</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{userDoc?.email}</div></div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group"><label className="form-label">الاسم</label><input className="form-input" {...register('name', { required: true })} /></div>
          <div className="form-group"><label className="form-label">رقم الهاتف</label><input className="form-input" {...register('phone')} /></div>
          <div className="form-group"><label className="form-label">رقم هاتف ولي الأمر</label><input className="form-input" {...register('parentPhone')} /></div>
          <div className="form-group">
            <label className="form-label">الصف الدراسي</label>
            <select className="form-input" {...register('grade')}>
              <option value="">-- اختر الصف --</option>
              {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">نبذة عنك</label><textarea className="form-input" rows={3} {...register('bio')} /></div>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>{isSubmitting ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}</button>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════ TEACHER DASHBOARD ══════════════════ */
function TeacherDashboard({ userDoc, refreshUserDoc }) {
  const [tab, setTab]             = useState('overview');
  const [earnings, setEarnings]   = useState({ transactions: [], totalEarnings: 0, thisMonthEarnings: 0, commission: 15 });
  const [enrollments, setEnroll]  = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editCourse, setEditCourse] = useState(null); // course being edited
  const pageRef = useScrollReveal();

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      getEarnings(),
      getTeacherEnrollments(),
      getMyCourses(userDoc.uid),
      getPublicStats()
    ])
      .then(([er, en, cr, sr]) => {
        setEarnings({ ...er.data, commission: sr.data.commission || 15 });
        setEnroll(en.data || []);
        setMyCourses(cr.data.courses || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const chartData = (() => {
    const months = {};
    (earnings.transactions || []).forEach(t => {
      const m = t.createdAt?.slice(0, 7);
      if (m) months[m] = (months[m] || 0) + t.teacherEarning;
    });
    return Object.entries(months).sort().map(([m, v]) => ({ شهر: m, أرباح: Math.round(v) }));
  })();

  const TABS = [
    { key: 'overview', label: '🏠 الرئيسية' },
    { key: 'analytics', label: '📊 التحليلات المتقدمة' },
    { key: 'courses',  label: `📚 كورساتي (${myCourses.length})` },
    { key: 'marketing', label: '📢 التسويق والنمو' },
    { key: 'coupons', label: '🎫 الكوبونات' },
    { key: 'subscription', label: '💎 خطتي' },
    { key: 'quizzes', label: '📝 إدارة الاختبارات' },
    { key: 'announcements', label: '📢 التنبيهات' },
    { key: 'students', label: `👥 الطلاب (${enrollments.length})` },
    { key: 'review',   label: '⭐ تقييم المنصة' },
    { key: 'create',   label: '➕ كورس جديد' },
    { key: 'profile',  label: '⚙️ ملفي الشخصي' },
  ];

  // If editing a course, show editor
  if (editCourse) {
    return (
      <div className="dashboard">
        <div className="db-header">
          <div className="db-header-inner">
            <button className="btn btn-outline btn-sm" onClick={() => setEditCourse(null)}>← رجوع</button>
            <h1 style={{ color: '#fff', fontSize: 18 }}>تعديل: {editCourse.title}</h1>
          </div>
        </div>
        <div className="db-body">
          <EditCourseForm
            course={editCourse}
            userDoc={userDoc}
            onDone={() => { setEditCourse(null); loadAll(); }}
          />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      className="dashboard"
      ref={pageRef}
    >
      <div className="db-header">
        <div className="db-header-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="avatar" style={{ width: 52, height: 52, fontSize: 20, flexShrink: 0 }}>
              {userDoc?.avatar
                ? <img src={userDoc.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : userDoc?.name?.[0] || '؟'
              }
            </div>
            <div>
              <h1>لوحة المدرّس — {userDoc?.name} 👨‍🏫</h1>
              <p>{userDoc?.isApproved ? '✅ حسابك نشط' : '⏳ حسابك قيد المراجعة'}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to={`/teachers/${userDoc?.username || userDoc?.uid}`} className="btn btn-outline btn-sm">👤 بروفايلي</Link>
            <Link to="/reels" className="btn btn-outline btn-sm">🎬 الريلز</Link>
          </div>
        </div>
      </div>

      <div className="db-body">
        <div className="db-tabs">
          {TABS.map(t => (
            <button key={t.key} className={`db-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === 'overview' && (
          <div>
            <div className="db-stats-row">
              <div className="db-stat-card"><span className="db-stat-num">{Math.round(earnings.totalEarnings || 0)}<span style={{ fontSize: 14 }}> ج.م</span></span><span className="db-stat-lbl">إجمالي الأرباح</span></div>
              <div className="db-stat-card"><span className="db-stat-num">{Math.round(earnings.thisMonthEarnings || 0)}<span style={{ fontSize: 14 }}> ج.م</span></span><span className="db-stat-lbl">هذا الشهر</span></div>
              <div className="db-stat-card"><span className="db-stat-num">{myCourses.length}</span><span className="db-stat-lbl">كورساتي</span></div>
              <div className="db-stat-card"><span className="db-stat-num">{enrollments.length}</span><span className="db-stat-lbl">طلابي</span></div>
              <div className="db-stat-card" style={{ border: '1px solid var(--blue)', background: 'rgba(26,155,213,0.05)' }}>
                <span className="db-stat-num" style={{ color: 'var(--blue)' }}>{userDoc?.plan?.toUpperCase() || 'STARTER'}</span>
                <span className="db-stat-lbl">خطتي الحالية</span>
              </div>
            </div>

            {/* Teacher Onboarding Checklist */}
            {myCourses.length === 0 && (
              <div className="card onboarding-card" style={{ padding: 24, marginTop: 20, border: '2px dashed var(--blue)', background: 'rgba(26,155,213,0.02)' }}>
                <h3 style={{ margin: '0 0 15px', color: 'var(--blue)', fontSize: 18 }}>🚀 ابدأ طريقك مع مَدْرَسَك في خطوات بسيطة</h3>
                <div className="onboarding-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
                  <div className="onboarding-step" style={{ padding: 15, background: '#fff', borderRadius: 12, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>👤</div>
                    <h4 style={{ margin: '0 0 5px', fontSize: 14 }}>1. كمّل بروفايلك</h4>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>أضف صورتك، اسم المستخدم المميز، وصور لموادك.</p>
                    <button className="btn btn-ghost btn-xs" style={{ marginTop: 10 }} onClick={() => setTab('profile')}>الملف الشخصي ←</button>
                  </div>
                  <div className="onboarding-step" style={{ padding: 15, background: '#fff', borderRadius: 12, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>🎬</div>
                    <h4 style={{ margin: '0 0 5px', fontSize: 14 }}>2. ارفع أول كورس</h4>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>ارفع دروسك، وضيف رابط جروب واتساب للطلاب.</p>
                    <button className="btn btn-primary btn-xs" style={{ marginTop: 10 }} onClick={() => setTab('create')}>إنشاء كورس ←</button>
                  </div>
                  <div className="onboarding-step" style={{ padding: 15, background: '#fff', borderRadius: 12, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>📢</div>
                    <h4 style={{ margin: '0 0 5px', fontSize: 14 }}>3. ابدأ التسويق</h4>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>استخدم روابط الإحالة عشان تكسب رصيد إضافي.</p>
                    <button className="btn btn-ghost btn-xs" style={{ marginTop: 10 }} onClick={() => setTab('marketing')}>مركز النمو ←</button>
                  </div>
                  <div className="onboarding-step" style={{ padding: 15, background: '#fff', borderRadius: 12, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>🤝</div>
                    <h4 style={{ margin: '0 0 5px', fontSize: 14 }}>4. شارك واكسب</h4>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>انسخ رابط بروفايلك وشاركه مع طلابك على السوشيال ميديا.</p>
                  </div>
                </div>
              </div>
            )}

            <ReferralCard userDoc={userDoc} isTeacher={true} />

            {chartData.length > 0 && (
              <div className="card" style={{ padding: 24, marginTop: 20 }}>
                <h3 className="db-section-title">الأرباح الشهرية</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="شهر" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="أرباح" stroke="var(--blue)" fill="var(--blue-light)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Courses */}
        {tab === 'courses' && (
          <TeacherCourseList
            courses={myCourses} loading={loading}
            onRefresh={loadAll}
            onEdit={setEditCourse}
          />
        )}

        {/* Announcements */}
        {tab === 'announcements' && (
          <TeacherAnnouncementsTab courses={myCourses} />
        )}

        {/* Analytics */}
        {tab === 'analytics' && (
          <TeacherAnalyticsTab userDoc={userDoc} setTab={setTab} />
        )}

        {/* Quizzes */}
        {tab === 'quizzes' && (
          <TeacherQuizManagement myCourses={myCourses} setEditCourse={setEditCourse} />
        )}

        {/* Coupons */}
        {tab === 'coupons' && (
          <TeacherCouponsTab courses={myCourses} />
        )}

        {/* Subscription */}
        {tab === 'subscription' && (
          <TeacherSubscriptionTab userDoc={userDoc} />
        )}

        {/* Marketing */}
        {tab === 'marketing' && (
          <TeacherMarketingTab userDoc={userDoc} myCourses={myCourses} />
        )}

        {/* Students */}
        {tab === 'students' && (
          <div>
            <h2 className="db-section-title">الطلاب ({enrollments.length})</h2>
            {enrollments.length === 0 ? (
              <div className="db-empty"><div style={{ fontSize: 48 }}>👥</div><h3>لا يوجد طلاب بعد</h3></div>
            ) : (
              <div className="card" style={{ overflowX: 'auto' }}>
                <table className="db-table">
                  <thead><tr><th>الطالب</th><th>الكورس</th><th>التقدم</th><th>تاريخ الاشتراك</th><th>أرباحك</th></tr></thead>
                  <tbody>
                    {enrollments.map((e, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 13 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {e.studentAvatar ? <img src={e.studentAvatar} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} /> : <span>👤</span>}
                            {e.studentName || 'طالب'}
                          </div>
                        </td>
                        <td style={{ fontSize: 12 }}>{e.courseTitle || 'كورس'}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="progress-bar" style={{ width: 70 }}><div className="progress-fill" style={{ width: `${e.progress || 0}%` }} /></div>
                            <span style={{ fontSize: 11 }}>{e.progress || 0}%</span>
                          </div>
                        </td>
                        <td style={{ fontSize: 11 }}>{new Date(e.enrolledAt).toLocaleDateString('ar-EG')}</td>
                        <td style={{ color: 'var(--success)', fontWeight: 700 }}>{Math.round(e.teacherEarning || 0)} ج.م</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Create */}
        {tab === 'create' && (
          <CreateCourseForm userDoc={userDoc} onCreated={() => { loadAll(); setTab('courses'); }} />
        )}

        {/* Review */}
        {tab === 'review' && (
          <PlatformReviewTab />
        )}

        {/* Profile */}
        {tab === 'profile' && (
          <TeacherProfileForm userDoc={userDoc} refreshUserDoc={refreshUserDoc} />
        )}
      </div>
    </motion.div>
  );
}

/* ─── Subscription Tab Component ─── */
function TeacherSubscriptionTab({ userDoc }) {
  const currentPlan = userDoc?.plan || 'starter';
  const plans = [
    { id: 'starter', name: 'الخطة الأساسية (Starter)', price: 'مجانية', features: ['كورس واحد فقط', '50 طالب كحد أقصى', 'علامة منصة مدرستك'] },
    { id: 'pro', name: 'الخطة الاحترافية (Pro)', price: '299 ج.م / شهر', features: ['5 كورسات كحد أقصى', '500 طالب كحد أقصى', 'تحليلات متقدمة', 'بدون علامة المنصة'] },
    { id: 'business', name: 'خطة الأعمال (Business)', price: '799 ج.م / شهر', features: ['كورسات غير محدودة', 'طلاب غير محدودين', 'أدوات الذكاء الاصطناعي', 'دعم فني مخصص'] },
  ];

  const [useReferral, setUseReferral] = useState(false);
  const handleUpgrade = async (planId) => {
    if (planId === currentPlan) return;
    if (planId === 'starter') {
      if (window.confirm('هل تريد العودة للخطة الأساسية؟ سيتم إيقاف المميزات الإضافية.')) {
        try { await updateMe({ plan: 'starter' }); window.location.reload(); } catch { toast.error('حدث خطأ'); }
      }
      return;
    }

    try {
      const { data } = await api.post('/payments/checkout', {
        purchaseType: 'subscription',
        planId,
        method: 'card',
        useReferralBalance: useReferral
      });
      if (data.enrolled) { toast.success('✅ تم الترقية بنجاح!'); window.location.reload(); }
      else if (data.iframeUrl) window.location.href = data.iframeUrl;
    } catch (err) {
      toast.error(err.response?.data?.error || 'فشل بدء عملية الدفع');
    }
  };

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 className="db-section-title" style={{ margin: 0 }}>إدارة اشتراكك 💎</h2>
        {userDoc?.planExpiresAt && (
          <div className="badge badge-amber" style={{ fontSize: 13, padding: '8px 16px' }}>
            ينتهي في: {new Date(userDoc.planExpiresAt).toLocaleDateString('ar-EG')}
          </div>
        )}
      </div>

      {userDoc?.referralBalance > 0 && (
        <div style={{ marginBottom: 20, padding: 15, background: 'rgba(26,155,213,0.05)', border: '1px solid var(--blue)', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--blue)' }}>💰 رصيد الإحالات المتاح: {userDoc.referralBalance} ج.م</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>يمكنك استخدام هذا الرصيد لترقية خطتك الحالية.</div>
          </div>
          <label className="switch" style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
            <input type="checkbox" checked={useReferral} onChange={e => setUseReferral(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, background: useReferral ? 'var(--blue)' : '#ccc', transition: '0.3s', borderRadius: 24 }}>
              <span style={{ position: 'absolute', height: 18, width: 18, left: useReferral ? 22 : 3, bottom: 3, background: '#fff', transition: '0.3s', borderRadius: '50%' }} />
            </span>
          </label>
        </div>
      )}

      <div className="grid-3">
        {plans.map(p => (
          <div key={p.id} className={`card plan-card ${currentPlan === p.id ? 'active' : ''}`} style={{ padding: 24, border: currentPlan === p.id ? '2px solid var(--blue)' : '1px solid var(--border)' }}>
            {currentPlan === p.id && <span className="badge badge-blue" style={{ marginBottom: 10 }}>خطتك الحالية</span>}
            <h3 style={{ marginBottom: 10 }}>{p.name}</h3>
            <div style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 20, color: 'var(--blue)' }}>{p.price}</div>
            <ul style={{ listStyle: 'none', padding: 0, marginBottom: 25 }}>
              {p.features.map((f, i) => <li key={i} style={{ marginBottom: 10, fontSize: 14 }}>✅ {f}</li>)}
            </ul>
            <button
              className={`btn ${currentPlan === p.id ? 'btn-ghost' : 'btn-primary'} w-100`}
              disabled={currentPlan === p.id}
              onClick={() => handleUpgrade(p.id)}
            >
              {currentPlan === p.id ? 'نشط حالياً' : 'ترقية الآن'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Teacher Marketing Tab ─── */
function TeacherMarketingTab({ userDoc, myCourses }) {
  const profileUrl = `${window.location.origin}/teachers/${userDoc?.username || userDoc?.uid}?ref=${userDoc?.referralCode}`;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('📋 تم نسخ الرابط!');
  };

  return (
    <div className="tab-content">
      <h2 className="db-section-title">مركز التسويق والنمو 🚀</h2>

      <div className="card" style={{ padding: 24, marginBottom: 20, background: 'linear-gradient(135deg, #1a9bd5 0%, #0dd4b8 100%)', color: '#fff' }}>
        <h3 style={{ margin: '0 0 10px' }}>📢 شارك رابطك واكسب!</h3>
        <p style={{ fontSize: 14, opacity: 0.9 }}>كل طالب يسجل من خلال رابطك، المنصة بتكافئك بـ 10 ج.م في رصيدك، وهوه بياخد 5 ج.م.</p>
        <div style={{ marginTop: 20, background: 'rgba(255,255,255,0.2)', padding: 15, borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 15 }}>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', opacity: 0.8, marginBottom: 5 }}>رابط بروفايلك الدعائي:</div>
            <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{profileUrl}</div>
          </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-sm" style={{ background: '#fff', color: '#1a9bd5', border: 'none', fontWeight: 800 }} onClick={() => copy(profileUrl)}>نسخ الرابط</button>
          <button className="btn btn-sm" style={{ background: '#25D366', color: '#fff', border: 'none', fontWeight: 800 }} onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`📚 تابع بروفايلي التعليمي على منصة مدرسك واستفيد من الكورسات والمراجعات:\n${profileUrl}`)}`, '_blank')}>💬 واتساب</button>
        </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card" style={{ padding: 20 }}>
          <h3 className="form-section-title">🔗 روابط الكورسات (مع كود الإحالة)</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 15 }}>استخدم الروابط دي لما تنشر على جروبات الفيسبوك وواتساب عشان تضمن وصول الطلاب وتستفيد من نظام الإحالة.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {myCourses.map(c => {
              const url = `${window.location.origin}/courses/${c.slug || c.id}?ref=${userDoc?.referralCode}`;
              return (
                <div key={c.id} style={{ padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 5 }}>{c.title}</div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input className="form-input" readOnly value={url} style={{ fontSize: 11, height: 32 }} />
                    <button className="btn btn-primary btn-xs" onClick={() => copy(url)}>نسخ</button>
                    <button className="btn btn-ghost btn-xs" style={{ color: '#25D366' }} onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`🚀 كورس جديد ومميز: "${c.title}"\nشوف التفاصيل من هنا واستفيد من العرض:\n${url}`)}`, '_blank')}>💬</button>
                  </div>
                </div>
              );
            })}
            {myCourses.length === 0 && <p style={{ fontSize: 13, textAlign: 'center', padding: '10px 0' }}>لا توجد كورسات بعد</p>}
          </div>
        </div>

        <div className="card" style={{ padding: 20 }}>
          <h3 className="form-section-title">📈 نصائح للنمو (Growth Hacks)</h3>
          <ul style={{ paddingRight: 20, fontSize: 13, lineHeight: 1.8, color: 'var(--text-main)' }}>
            <li><strong>جروبات الواتساب:</strong> اعمل جروب واتساب لكل كورس وضيف الرابط في إعدادات الكورس. ده بيخلي الطلاب يحسوا بمجتمع حقيقي.</li>
            <li><strong>أول درس مجاني:</strong> اتأكد إن أول درس في الكورس دايماً يكون جذاب جداً ومفيد، ده اللي بيقنع الطالب يشتري.</li>
            <li><strong>السوشيال ميديا:</strong> انشر "ريلز" (Reels) قصيرة فيها معلومة سريعة وحط رابط بروفايلك في الكومنتات.</li>
            <li><strong>التقييمات:</strong> اطلب من طلابك يقيموا الكورس. التقييمات العالية بتخلي كورساتك تظهر في أول نتايج البحث.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ─── Teacher Coupons Tab ─── */
function TeacherCouponsTab({ courses }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm({
    defaultValues: {
      discountType: 'percentage',
      usageLimit: 100,
      expirationDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    }
  });

  const loadCoupons = async () => {
    setLoading(true);
    try {
      const r = await api.get('/coupons');
      setCoupons(r.data || []);
    } catch { toast.error('فشل تحميل الكوبونات'); }
    setLoading(false);
  };

  useEffect(() => { loadCoupons(); }, []);

  const onAdd = async (data) => {
    try {
      const payload = {
        ...data,
        applicableCourses: data.applicableCourses === 'all' ? [] : [data.applicableCourses],
      };
      await api.post('/coupons', payload);
      toast.success('✅ تم إنشاء الكوبون');
      reset(); setShowAdd(false); loadCoupons();
    } catch (err) { toast.error(err.response?.data?.error || 'حدث خطأ'); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('حذف هذا الكوبون؟')) return;
    try {
      await api.delete(`/coupons/${id}`);
      toast.success('تم الحذف');
      loadCoupons();
    } catch { toast.error('فشل الحذف'); }
  };

  const toggleActive = async (coupon) => {
    try {
      await api.patch(`/coupons/${coupon.id}`, { active: !coupon.active });
      loadCoupons();
    } catch { toast.error('حدث خطأ'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 className="db-section-title" style={{ margin: 0 }}>إدارة الكوبونات 🎫</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? 'إلغاء' : '+ إنشاء كوبون جديد'}
        </button>
      </div>

      {showAdd && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <form onSubmit={handleSubmit(onAdd)}>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">كود الخصم *</label>
                <input className="form-input" placeholder="مثال: SAVE20" {...register('code', { required: true })} />
              </div>
              <div className="form-group">
                <label className="form-label">الكورس *</label>
                <select className="form-input" {...register('applicableCourses', { required: true })}>
                  <option value="all">كل كورساتي</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
            </div>
            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">نوع الخصم</label>
                <select className="form-input" {...register('discountType')}>
                  <option value="percentage">نسبة مئوية (%)</option>
                  <option value="fixed">مبلغ ثابت (ج.م)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">القيمة *</label>
                <input type="number" className="form-input" {...register('discountValue', { required: true })} />
              </div>
              <div className="form-group">
                <label className="form-label">تاريخ الانتهاء *</label>
                <input type="date" className="form-input" {...register('expirationDate', { required: true })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">حد الاستخدام (مرات)</label>
              <input type="number" className="form-input" {...register('usageLimit')} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'جاري الحفظ...' : 'حفظ الكوبون'}
            </button>
          </form>
        </div>
      )}

      {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div> : (
        <div className="card" style={{ overflowX: 'auto' }}>
          {coupons.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>لا توجد كوبونات بعد.</p>
          ) : (
            <table className="db-table">
              <thead>
                <tr>
                  <th>الكود</th>
                  <th>الخصم</th>
                  <th>الاستخدام</th>
                  <th>الحالة</th>
                  <th>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => (
                  <tr key={c.id}>
                    <td><strong style={{ color: 'var(--blue)' }}>{c.code}</strong></td>
                    <td style={{ fontSize: 13 }}>
                      {c.discountValue}{c.discountType === 'percentage' ? '%' : ' ج.م'}
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                        {c.applicableCourses?.length === 0 ? 'كل الكورسات' : 'كورسات محددة'}
                      </div>
                    </td>
                    <td style={{ fontSize: 12 }}>{c.usedCount} / {c.usageLimit}</td>
                    <td>
                      <button
                        onClick={() => toggleActive(c)}
                        className={`badge ${c.active ? 'badge-green' : 'badge-amber'}`}
                        style={{ border: 'none', cursor: 'pointer' }}
                      >
                        {c.active ? 'نشط' : 'معطل'}
                      </button>
                    </td>
                    <td>
                      <button onClick={() => onDelete(c.id)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Teacher Announcements Tab ─── */
function TeacherAnnouncementsTab({ courses }) {
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm();

  const load = async (courseId) => {
    if (!courseId) return;
    setLoading(true);
    try {
      let r;
      if (courseId === 'all') {
        r = await api.get('/announcements/teacher/all');
        setAnnouncements(r.data.filter(a => a.courseId === 'all') || []);
      } else {
        r = await getAnnouncements(courseId);
        setAnnouncements(r.data || []);
      }
    } catch { toast.error('فشل تحميل التنبيهات'); }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedCourseId) load(selectedCourseId);
    else { setAnnouncements([]); setShowAdd(false); }
  }, [selectedCourseId]);

  const onAdd = async (data) => {
    try {
      await createAnnouncement({ ...data, courseId: selectedCourseId });
      toast.success('✅ تم نشر التنبيه');
      reset(); setShowAdd(false); load(selectedCourseId);
    } catch { toast.error('حدث خطأ'); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('حذف هذا التنبيه؟')) return;
    try {
      await deleteAnnouncement(id);
      toast.success('تم الحذف');
      load(selectedCourseId);
    } catch { toast.error('فشل الحذف'); }
  };

  return (
    <div>
      <h2 className="db-section-title">إدارة تنبيهات الكورس 📢</h2>
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div className="form-group">
          <label className="form-label">اختر الكورس (أو كل الطلاب)</label>
          <select className="form-input" value={selectedCourseId} onChange={e => setSelectedCourseId(e.target.value)}>
            <option value="">-- اختر كورس لعرض تنبيهاته --</option>
            <option value="all" style={{ fontWeight: 700, color: 'var(--blue)' }}>📢 إرسال لجميع طلابي (في كل الكورسات)</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
      </div>

      {selectedCourseId && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="form-section-title" style={{ margin: 0 }}>التنبيهات ({announcements.length})</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
              {showAdd ? 'إلغاء' : '+ إضافة تنبيه'}
            </button>
          </div>

          {showAdd && (
            <div className="card" style={{ padding: 20, marginBottom: 20 }}>
              <form onSubmit={handleSubmit(onAdd)}>
                <div className="form-group">
                  <label className="form-label">العنوان</label>
                  <input className="form-input" placeholder="مثال: موعد الحصة القادمة" {...register('title', { required: true })} />
                </div>
                <div className="form-group">
                  <label className="form-label">المحتوى</label>
                  <textarea className="form-input" rows={4} placeholder="اكتب تفاصيل التنبيه هنا..." {...register('content', { required: true })} />
                </div>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'جاري النشر...' : 'نشر التنبيه'}
                </button>
              </form>
            </div>
          )}

          {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}><div className="spinner" /></div> : (
            <div className="announcements-list">
              {announcements.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>لا توجد تنبيهات لهذا الكورس بعد.</p>
              ) : announcements.map(a => (
                <div key={a.id} className="announcement-item card" style={{ padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <strong style={{ color: 'var(--blue)' }}>{a.title}</strong>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(a.createdAt).toLocaleDateString('ar-EG')}</span>
                      <button
                        className="btn btn-ghost btn-xs"
                        style={{ color: '#25D366' }}
                        title="نشر عبر واتساب"
                        onClick={() => {
                          const text = `📢 *تنبيه جديد من مدرسك*\n\n*${a.title}*\n\n${a.content}\n\n🔗 رابط الكورس: ${window.location.origin}/courses/${a.courseSlug || a.courseId}`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                        }}
                      >
                        <span style={{ fontSize: 16 }}>💬</span>
                      </button>
                      <button onClick={() => onDelete(a.id)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>🗑</button>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{a.content}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Teacher Course List ─── */
/* ─── Teacher Analytics Tab ─── */
function TeacherAnalyticsTab({ userDoc, setTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getTeacherAnalytics()
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.error || 'فشل تحميل البيانات'))
      .finally(() => setLoading(false));
  }, []);

  if (userDoc?.plan === 'starter' || !userDoc?.plan) {
    return (
      <div className="card" style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 50, marginBottom: 20 }}>📊</div>
        <h2 style={{ marginBottom: 10 }}>التحليلات المتقدمة غير متاحة</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 25 }}>
          قم بترقية خطتك إلى <strong>Pro</strong> أو <strong>Business</strong> لمشاهدة رسوم بيانية مفصلة عن أرباحك وتقدم طلابك.
        </p>
        <button className="btn btn-primary" onClick={() => {
           if (setTab) setTab('subscription');
           else toast.info('انتقل لتبويب "خطتي" للترقية 💎');
        }}>اكتشف الخطط المتاحة</button>
      </div>
    );
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;
  if (error) return <div className="db-empty"><h3>{error}</h3></div>;
  if (!data) return <div className="db-empty"><h3>لا توجد بيانات تحليلية متاحة حالياً</h3></div>;

  return (
    <div className="tab-content">
      <h2 className="db-section-title">إحصائيات الأداء 📊</h2>

      <div className="grid-2">
        {/* Earnings Growth */}
        <div className="card" style={{ padding: 20 }}>
          <h3 className="db-section-title" style={{ fontSize: 16 }}>نمو الأرباح (ج.م)</h3>
          <div style={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.earningsData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="value" stroke="#1a9bd5" fill="#1a9bd5" fillOpacity={0.1} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Student Progress Pie */}
        <div className="card" style={{ padding: 20 }}>
          <h3 className="db-section-title" style={{ fontSize: 16 }}>توزيع تقدم الطلاب</h3>
          <div style={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.progressData}
                  cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" label
                >
                  {[
                    { name: '0-25%', color: '#ef4444' },
                    { name: '26-50%', color: '#f59e0b' },
                    { name: '51-75%', color: '#1a9bd5' },
                    { name: '76-100%', color: '#0dd4b8' },
                  ].map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Course Popularity */}
      <div className="card" style={{ padding: 20, marginTop: 20 }}>
        <h3 className="db-section-title" style={{ fontSize: 16 }}>عدد الطلاب لكل كورس</h3>
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.courseData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#0dd4b8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function TeacherCourseList({ courses, loading, onRefresh, onEdit }) {
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 className="db-section-title" style={{ margin: 0 }}>كورساتي ({courses.length})</h2>
      </div>
      {courses.length === 0 ? (
        <div className="db-empty">
          <div style={{ fontSize: 48 }}>📚</div><h3>لم تنشئ أي كورس بعد</h3>
        </div>
      ) : (
        <div className="teacher-courses-list">
          {courses.map(c => (
            <div key={c.id} className="teacher-course-card card">
              <div className="tcc-thumb">
                {c.thumbnail ? <img src={c.thumbnail} alt={c.title} /> : <div className="tcc-thumb-ph">{c.subject?.[0]}</div>}
              </div>
              <div className="tcc-body">
                <div className="tcc-title">{c.title}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  {c.isPublished
                    ? <span className="badge badge-green" style={{ fontSize: 11 }}>✅ منشور</span>
                    : c.pendingReview
                    ? <span className="badge badge-amber" style={{ fontSize: 11 }}>⏳ قيد المراجعة</span>
                    : <span className="badge badge-amber" style={{ fontSize: 11 }}>📝 مسودة</span>}
                  {c.grade && <span className="badge badge-blue" style={{ fontSize: 11 }}>🎓 {c.grade}</span>}
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-muted)' }}>
                  <span>💰 {c.price === 0 ? 'مجاني' : `${c.price} ج.م`}</span>
                  <span>👥 {c.enrolledCount || 0} طالب</span>
                  <span>📖 {c.lessons?.length || 0} درس</span>
                </div>
              </div>
              <div className="tcc-actions">
                <Link to={`/courses/${c.slug || c.id}`} className="btn btn-outline btn-sm">معاينة</Link>
                <button className="btn btn-primary btn-sm" onClick={() => onEdit(c)}>✏️ تعديل</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Edit Course Form (published course: add lesson + PDFs + banner) ─── */
function EditCourseForm({ course, userDoc, onDone }) {
  const [title, setTitle]             = useState(course.title || '');
  const [description, setDescription] = useState(course.description || '');
  const [price, setPrice]             = useState(course.price || 0);
  const [subject, setSubject]         = useState(course.subject || '');
  const [grade, setGrade]             = useState(course.grade || '');
  const [slug, setSlug]               = useState(course.slug || '');
  const [lessons, setLessons]         = useState(course.lessons || []);
  const [saving, setSaving]           = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [thumbUploading, setThumbUploading]   = useState(false);
  const [bannerUrl, setBannerUrl]     = useState(course.banner || '');
  const [thumbUrl, setThumbUrl]       = useState(course.thumbnail || '');
  const [waLink, setWaLink]           = useState(course.whatsappGroupLink || '');
  const [certEnabled, setCertEnabled] = useState(course.isCertificateEnabled || false);
  const [paymentType, setPaymentType] = useState(course.paymentType || 'full');
  const [quizStrategy, setQuizStrategy] = useState(course.quizStrategy || 'per_lesson');
  const [allowCredits, setAllowCredits] = useState(course.allowCredits || false);
  const bannerInputRef = useRef();
  const thumbInputRef  = useRef();


  const handleBannerUpload = async (file) => {
    if (!file) return;
    setBannerUploading(true);
    try {
      const url = await uploadCourseBannerFile(file, course.id);
      setBannerUrl(url);
      toast.success('✅ تم رفع البانر!');
    } catch { toast.error('فشل رفع البانر'); }
    setBannerUploading(false);
  };

  const handleThumbUpload = async (file) => {
    if (!file) return;
    setThumbUploading(true);
    try {
      const url = await uploadCourseThumbnail(file);
      setThumbUrl(url);
      // Save immediately
      await updateCourse(course.id, { thumbnail: url });
      toast.success('✅ تم رفع الصورة المصغرة!');
    } catch { toast.error('فشل رفع الصورة'); }
    setThumbUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCourse(course.id, {
        title,
        description,
        price: paymentType === 'perLesson' ? 0 : parseFloat(price) || 0,
        subject,
        grade,
        slug,
        lessons: lessons.map(l => ({
          id:                 l.id,
          title:              l.title,
          isFree:             l.isFree,
          cloudinaryPublicId: l.cloudinaryPublicId || '',
          directVideoUrl:     l.directVideoUrl     || '',
          duration:           l.duration           || '',
          keyPoints:          l.keyPoints          || [],
          summary:            l.summary            || '',
          price:              paymentType === 'perLesson' ? (parseFloat(l.price) || 0) : 0,
        })),
        thumbnail: thumbUrl,
        banner: bannerUrl,
        whatsappGroupLink: waLink,
        isCertificateEnabled: certEnabled,
        paymentType,
        quizStrategy,
        allowCredits,
      });
      toast.success('✅ تم حفظ التغييرات!');
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'حدث خطأ');
    }
    setSaving(false);
  };

  return (
    <div className="create-course-wrap" style={{ maxWidth: 760 }}>

      {/* Basic Info */}
      <div className="card" style={{ marginBottom: 20, padding: 20 }}>
        <h3 className="form-section-title">📝 معلومات الكورس الأساسية</h3>
        <div className="form-group">
          <label className="form-label">رابط جروب واتساب للطلاب (اختياري)</label>
          <input className="form-input" placeholder="https://chat.whatsapp.com/..." value={waLink} onChange={e => setWaLink(e.target.value)} dir="ltr" />
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>يظهر فقط للطلاب المشتركين في الكورس.</p>
        </div>
        <div className="form-group">
          <label className="form-label">الرابط المختصر (Slug) *</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, direction: 'ltr' }}>
            <span style={{ color: 'var(--text-muted)' }}>madrasak.eg/courses/</span>
            <input
              className="form-input"
              value={slug}
              onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9\u0600-\u06FF]/g, '-').replace(/-+/g, '-'))}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">عنوان الكورس *</label>
          <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">وصف الكورس</label>
          <textarea className="form-input" rows={4} value={description} onChange={e => setDescription(e.target.value)} />
        </div>
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">المادة *</label>
            <SubjectSelector value={subject} onChange={setSubject} />
          </div>
          <div className="form-group">
            <label className="form-label">الصف الدراسي</label>
            <select className="form-input" value={grade} onChange={e => setGrade(e.target.value)}>
              <option value="">-- اختر الصف (اختياري) --</option>
              {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 10, padding: 12, background: 'rgba(13,212,184,0.05)', border: '1px solid var(--teal)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 13 }}>
            <span style={{ display: 'block', fontWeight: 700, color: 'var(--teal)' }}>🎁 قبول رصيد الهدايا كشراكة مع المنصة</span>
            <span style={{ fontSize: 11, opacity: 0.8 }}>هل تسمح للطلاب باستخدام رصيدهم لشراء هذا الكورس؟</span>
          </div>
          <label className="switch" style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
            <input type="checkbox" checked={allowCredits} onChange={e => setAllowCredits(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, background: allowCredits ? 'var(--teal)' : '#ccc', transition: '0.3s', borderRadius: 24 }}>
              <span style={{ position: 'absolute', height: 18, width: 18, left: allowCredits ? 22 : 3, bottom: 3, background: '#fff', transition: '0.3s', borderRadius: '50%' }} />
            </span>
          </label>
        </div>
      </div>

      {/* Slug Edit */}
      <div className="card" style={{ marginBottom: 20, padding: 20 }}>
        <h3 className="form-section-title">🔗 رابط الكورس (Slug)</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, direction: 'ltr' }}>
          <span style={{ color: 'var(--text-muted)' }}>madrasak.eg/courses/</span>
          <input
            className="form-input"
            value={slug}
            onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-\u0600-\u06FF]/g, '-'))}
          />
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, textAlign: 'right' }}>
          الرابط المختصر الذي سيظهر في المتصفح. يمكنك تغييره هنا.
        </p>
      </div>

      {/* Banner upload */}
      <div className="card" style={{ marginBottom: 20, overflow: 'hidden' }}>
        <div
          className="course-banner-preview"
          style={{ backgroundImage: bannerUrl ? `url(${bannerUrl})` : undefined }}
        >
          {!bannerUrl && <div className="banner-placeholder-text">📸 صورة البانر (1200×400)</div>}
          <button
            className="banner-upload-btn"
            onClick={() => bannerInputRef.current?.click()}
            disabled={bannerUploading}
          >
            {bannerUploading ? '⏳ جارٍ الرفع...' : '📸 ' + (bannerUrl ? 'تغيير البانر' : 'رفع بانر للكورس')}
          </button>
          <input ref={bannerInputRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => e.target.files[0] && handleBannerUpload(e.target.files[0])} />
        </div>
      </div>

      {/* Certificate Toggle */}
      <div className="card" style={{ marginBottom: 20, padding: 20, border: '1px solid var(--blue)', background: 'rgba(26,155,213,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>🎓 تفعيل الشهادات للطلاب</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>إذا فعلت هذا الخيار، سيتمكن الطلاب الذين يكملون الكورس بنسبة 100% من الحصول على شهادة إنجاز.</p>
          </div>
          <label className="free-toggle" style={{ fontSize: 20 }}>
            <input type="checkbox" checked={certEnabled} onChange={e => setCertEnabled(e.target.checked)} />
            <span className={`badge ${certEnabled ? 'badge-green' : 'badge-amber'}`}>
              {certEnabled ? 'مفعلة' : 'معطلة'}
            </span>
          </label>
        </div>
      </div>

      {/* Quiz Strategy */}
      <div className="card" style={{ marginBottom: 20, padding: 20 }}>
        <h3 className="form-section-title">📝 نظام الاختبارات (Quiz Strategy)</h3>
        <div className="form-group">
          <select
            className="form-input"
            value={quizStrategy}
            onChange={e => setQuizStrategy(e.target.value)}
          >
            <option value="per_lesson">📖 اختبار بعد كل درس (اختياري)</option>
            <option value="per_unit">🗂️ اختبار بعد كل وحدة (كل 5 دروس مثلاً)</option>
            <option value="end_of_course">🎓 اختبار نهائي واحد في نهاية الكورس</option>
          </select>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            {quizStrategy === 'per_lesson' && 'سيظهر التبويب الخاص بالاختبار في كل درس يحتوي على كويز.'}
            {quizStrategy === 'per_unit' && 'يمكنك تعيين الكويز لدروس معينة لتمثيل نهاية الوحدات.'}
            {quizStrategy === 'end_of_course' && 'سيظهر الاختبار فقط في آخر درس من دروس الكورس كاختبار نهائي.'}
          </p>
        </div>
      </div>

      {/* Payment Type Toggle */}
      <div className="card" style={{ marginBottom: 20, padding: 20 }}>
        <h3 className="form-section-title">💰 نظام البيع والتسعير</h3>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <button
            type="button"
            className={`btn ${paymentType === 'full' ? 'btn-primary' : 'btn-outline'}`}
            style={{ flex: 1 }}
            onClick={() => setPaymentType('full')}
          >
            📦 شراء الكورس كاملاً
          </button>
          <button
            type="button"
            className={`btn ${paymentType === 'perLesson' ? 'btn-primary' : 'btn-outline'}`}
            style={{ flex: 1 }}
            onClick={() => setPaymentType('perLesson')}
          >
            🎟️ الدفع لكل درس منفصلاً
          </button>
        </div>

        {paymentType === 'full' && (
          <div className="form-group">
            <label className="form-label">السعر الكلي (ج.م)</label>
            <input type="number" className="form-input" min={0} value={price} onChange={e => setPrice(e.target.value)} />
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>اكتب 0 لو الكورس مجاني</p>

            {price > 0 && (
              <div style={{ marginTop: 15, padding: 15, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                  <span>💰 سعر الكورس:</span>
                  <span>{price} ج.م</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5, color: '#ef4444' }}>
                  <span>📉 عمولة المنصة ({earnings.commission}%):</span>
                  <span>-{(price * (earnings.commission / 100)).toFixed(2)} ج.م</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: 'var(--success)', borderTop: '1px solid #bbf7d0', paddingTop: 8, marginTop: 5 }}>
                  <span>✨ صافي ربحك:</span>
                  <span>{(price * (1 - earnings.commission / 100)).toFixed(2)} ج.م</span>
                </div>
              </div>
            )}
          </div>
        )}

        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          {paymentType === 'full'
            ? 'سيقوم الطالب بدفع مبلغ واحد للوصول لكافة دروس الكورس.'
            : 'يستطيع الطالب اختيار وشراء دروس معينة فقط. تأكد من تحديد سعر لكل درس بالأسفل.'}
        </p>
      </div>

      {/* Thumbnail */}
      <div className="card" style={{ marginBottom: 20, padding: 20 }}>
        <h3 className="form-section-title">الصورة المصغرة (Thumbnail)</h3>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {thumbUrl && <img src={thumbUrl} alt="" style={{ width: 160, height: 90, objectFit: 'cover', borderRadius: 8 }} />}
          <div>
            <button className="btn btn-outline btn-sm" onClick={() => thumbInputRef.current?.click()} disabled={thumbUploading}>
              {thumbUploading ? '⏳ جارٍ الرفع...' : '📷 ' + (thumbUrl ? 'تغيير الصورة' : 'رفع صورة مصغرة')}
            </button>
            <input ref={thumbInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => e.target.files[0] && handleThumbUpload(e.target.files[0])} />
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>800×450px — JPG/PNG/WebP</p>
          </div>
        </div>
      </div>

      <LessonsBuilder lessons={lessons} setLessons={setLessons} paymentType={paymentType} courseId={course.id} />

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', padding: 14 }} onClick={handleSave} disabled={saving}>
          {saving ? 'جارٍ الحفظ...' : '💾 حفظ جميع التغييرات'}
        </button>
        <button className="btn btn-outline" onClick={onDone}>إلغاء</button>
      </div>
    </div>
  );
}

/* ─── Lessons Builder ─── */
function LessonsBuilder({ lessons, setLessons, paymentType = 'full', courseId }) {
  const addLesson = () => setLessons(prev => [
    ...prev,
    {
      id:                 Date.now().toString(),
      title:              '',
      isFree:             prev.length === 0,
      cloudinaryPublicId: '',
      directVideoUrl:     '',
      duration:           '',
      uploading:          false,
      uploadProgress:     0,
      price:              0,
      keyPoints:          [],
      summary:            '',
    }
  ]);

  const updateLesson = (id, key, val) =>
    setLessons(prev => prev.map(l => l.id === id ? { ...l, [key]: val } : l));
  const removeLesson = (id) => setLessons(prev => prev.filter(l => l.id !== id));

  const handleVideoUpload = async (lessonId, file) => {
    if (!file) return;
    updateLesson(lessonId, 'uploading', true);
    updateLesson(lessonId, 'uploadProgress', 0);
    const fd = new FormData();
    fd.append('video', file);
    try {
      const res = await uploadVideo(fd, pct => updateLesson(lessonId, 'uploadProgress', pct));
      updateLesson(lessonId, 'cloudinaryPublicId', res.data.publicId);
      const dur = res.data.duration;
      if (dur) updateLesson(lessonId, 'duration', `${Math.floor(dur/60)}:${String(Math.floor(dur%60)).padStart(2,'0')}`);
      toast.success('✅ تم رفع الفيديو بنجاح!');
    } catch (err) {
      toast.error('فشل رفع الفيديو — ' + (err.response?.data?.error || err.message));
    }
    updateLesson(lessonId, 'uploading', false);
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <h3 className="form-section-title" style={{ margin:0 }}>
          الدروس ({lessons.length})
          {paymentType === 'perLesson' && (
            <span style={{ fontSize:12, fontWeight:600, color:'#7c3aed', marginRight:10, background:'rgba(124,58,237,.1)', padding:'2px 8px', borderRadius:20 }}>
              🎟️ درس بدرس — حدد سعر كل درس
            </span>
          )}
        </h3>
        <button type="button" className="btn btn-outline btn-sm" onClick={addLesson}>
          + إضافة درس
        </button>
      </div>

      {lessons.length === 0 ? (
        <div className="lessons-empty">
          <p>لا توجد دروس — اضغط "إضافة درس" لتبدأ</p>
          <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
            أول درس يكون مجانياً تلقائياً 🎁
          </p>
        </div>
      ) : (
        <div className="lessons-builder-list">
          {lessons.map((lesson, i) => (
            <LessonBuilderItem
              key={lesson.id}
              lesson={lesson}
              index={i}
              courseId={courseId}
              paymentType={paymentType}
              updateLesson={updateLesson}
              removeLesson={removeLesson}
              handleVideoUpload={handleVideoUpload}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Lesson Builder Item with PDF upload ─── */
function LessonBuilderItem({ lesson, index, courseId, onUpdate, onRemove, onVideoUpload, isOwner, paymentType, updateLesson, removeLesson, handleVideoUpload }) {
  const inputRef = useRef();
  const [pdfs, setPdfs]           = useState([]);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [pdfFile, setPdfFile]     = useState(null);
  const [pdfName, setPdfName]     = useState('');
  const [uploading, setUploading] = useState(false);
  const [showPdfs, setShowPdfs]   = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [kpInput,   setKpInput]   = useState('');

  const upL = updateLesson || onUpdate;
  const remL = removeLesson || onRemove;
  const vidL = handleVideoUpload || onVideoUpload;

  const lessonIndex = index;

  const loadPdfs = async () => {
    if (!courseId) return;
    setLoadingPdfs(true);
    try {
      const { data } = await getCoursePdfs(courseId, lessonIndex);
      setPdfs(data || []);
    } catch (_) { setPdfs([]); }
    setLoadingPdfs(false);
  };

  const handleShowPdfs = () => {
    if (!showPdfs) loadPdfs();
    setShowPdfs(v => !v);
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) return toast.error('اختر ملف PDF');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('pdf', pdfFile);
      fd.append('courseId', courseId);
      fd.append('lessonIndex', lessonIndex);
      fd.append('fileName', pdfName || pdfFile.name);
      await uploadPdf(fd);
      toast.success('✅ تم رفع الملف!');
      setPdfFile(null); setPdfName('');
      loadPdfs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'فشل الرفع');
    }
    setUploading(false);
  };

  const handlePdfDelete = async (id, name) => {
    if (!window.confirm(`حذف "${name}"؟`)) return;
    try { await deletePdf(id); setPdfs(p => p.filter(x => x.id !== id)); toast.success('تم الحذف'); }
    catch { toast.error('فشل الحذف'); }
  };

  const addKP = () => {
    const t = kpInput.trim();
    if (!t) return;
    upL(lesson.id, 'keyPoints', [...(lesson.keyPoints || []), t]);
    setKpInput('');
  };

  const isFirstLesson = index === 0;

  return (
    <div className={`lesson-builder-item ${lesson.isNew ? 'lesson-new' : ''}`}>
      <div className="lesson-builder-header">
        <span className="lesson-builder-num">{index + 1}</span>
        <input className="form-input" placeholder="عنوان الدرس *"
          value={lesson.title} onChange={e => upL(lesson.id, 'title', e.target.value)}
          style={{ flex: 1 }} />

        {/* Free toggle — always shown */}
        {isFirstLesson ? (
          <span style={{ fontSize:12, color:'var(--success)', fontWeight:600, background:'#dcfce7', padding:'3px 10px', borderRadius:20 }}>
            مجاني 🎁
          </span>
        ) : (
          <label className="free-toggle">
            <input type="checkbox" checked={lesson.isFree}
              onChange={e => upL(lesson.id, 'isFree', e.target.checked)} />
            <span>مجاني 🎁</span>
          </label>
        )}

        {/* Per-lesson price field */}
        {paymentType === 'perLesson' && !isFirstLesson && !lesson.isFree && (
          <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
            <input
              type="number"
              className="form-input"
              min={0}
              placeholder="السعر"
              value={lesson.price || ''}
              onChange={e => upL(lesson.id, 'price', parseFloat(e.target.value) || 0)}
              style={{ width:90 }}
            />
            <span style={{ fontSize:12, color:'var(--text-muted)', whiteSpace:'nowrap' }}>ج.م</span>
          </div>
        )}

        <button type="button" className="btn btn-ghost btn-xs" onClick={() => remL(lesson.id)} title="حذف الدرس">🗑</button>
      </div>

      {/* Video upload area */}
      <div className="lesson-upload-area">
        {lesson.cloudinaryPublicId ? (
          <div className="upload-success">
            <span>✅ فيديو على Cloudinary</span>
            {lesson.duration && <span className="upload-duration">⏱ {lesson.duration}</span>}
            <span className="upload-duration">🔒 محمي</span>
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => { upL(lesson.id, 'cloudinaryPublicId', ''); upL(lesson.id, 'duration', ''); }}>تغيير</button>
          </div>
        ) : lesson.directVideoUrl ? (
          <div className="upload-success">
            <span>🔗 رابط فيديو</span>
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => upL(lesson.id, 'directVideoUrl', '')}>تغيير</button>
          </div>
        ) : lesson.uploading ? (
          <div className="upload-progress-wrap">
            <div className="upload-progress-label">جارٍ رفع الفيديو... {lesson.uploadProgress}%</div>
            <div className="upload-progress-bar"><div className="upload-progress-fill" style={{ width: `${lesson.uploadProgress}%` }} /></div>
          </div>
        ) : (
          <div className="lesson-video-options">
            <div className="video-upload-btn" onClick={() => inputRef.current?.click()}>
              <span style={{ fontSize: 26 }}>🎬</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>ارفع فيديو الدرس</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>MP4 / WebM — حتى 500MB</div>
              </div>
              <input ref={inputRef} type="file" accept="video/*" style={{ display: 'none' }}
                onChange={e => e.target.files[0] && vidL(lesson.id, e.target.files[0])} />
            </div>
            <div className="video-or-divider">أو</div>
            <input className="form-input" placeholder="الصق رابط الفيديو (YouTube / MP4)"
              defaultValue={lesson.directVideoUrl || ''} dir="ltr"
              onBlur={e => { if (e.target.value.trim()) upL(lesson.id, 'directVideoUrl', e.target.value.trim()); }} />
          </div>
        )}
      </div>

      {/* Learning content toggle */}
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        <button type="button" className="btn btn-ghost btn-xs"
          style={{ display:'flex', alignItems:'center', gap:5 }}
          onClick={() => setShowExtra(!showExtra)}>
          {showExtra ? '▲' : '▼'} محتوى تعليمي
        </button>

        <button type="button" className={`btn btn-xs ${lesson.quiz ? 'btn-primary' : 'btn-outline'}`}
          style={{ display:'flex', alignItems:'center', gap:5 }}
          onClick={async () => {
            if (courseId && !lesson.quiz) {
              setLoadingQuiz(true);
              try {
                const { data } = await getLessonQuiz(courseId, index);
                if (data) upL(lesson.id, 'quiz', data);
              } catch (_) {}
              setLoadingQuiz(false);
            }
            setShowQuizModal(true);
          }}
          disabled={loadingQuiz}
        >
          {loadingQuiz ? '...' : (lesson.quiz ? '📝 تعديل الاختبار' : '📝 إضافة اختبار')}
        </button>
      </div>

      {showQuizModal && (
        <QuizModal
          lessonTitle={lesson.title || `الدرس ${index + 1}`}
          initialQuiz={lesson.quiz}
          onSave={async (quizData) => {
            try {
              if (courseId) {
                // Edit mode: save immediately
                if (lesson.quiz?.id) {
                  await updateQuiz(lesson.quiz.id, { ...quizData, courseId, lessonIndex: index });
                  toast.success('✅ تم تحديث الاختبار بنجاح');
                } else {
                  const res = await createQuiz({ ...quizData, courseId, lessonIndex: index });
                  quizData.id = res.data.id;
                  toast.success('✅ تم إنشاء الاختبار بنجاح');
                }
              }
              upL(lesson.id, 'quiz', quizData);
              setShowQuizModal(false);
              if (!courseId) toast.success('✅ تم حفظ بيانات الاختبار محلياً');
            } catch (err) {
              toast.error(err.response?.data?.error || 'فشل حفظ الاختبار');
            }
          }}
          onClose={() => setShowQuizModal(false)}
        />
      )}

      {showExtra && (
        <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:10, padding:12, background:'#fffbeb', borderRadius:8, border:'1px solid #fde68a' }}>
          {/* Key points */}
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">💡 النقاط الرئيسية</label>
            <div style={{ display:'flex', gap:6, marginBottom:8 }}>
              <input className="form-input" placeholder="أضف نقطة رئيسية..."
                value={kpInput} onChange={e => setKpInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKP())}
                style={{ flex:1 }} />
              <button type="button" className="btn btn-outline btn-sm" onClick={addKP}>+</button>
            </div>
            {(lesson.keyPoints || []).map((kp, ki) => (
              <div key={ki} style={{ display:'flex', alignItems:'center', gap:8, background:'#fff', padding:'5px 10px', borderRadius:6, marginBottom:4, border:'1px solid #fde68a' }}>
                <span style={{ color:'#f59e0b' }}>✦</span>
                <span style={{ flex:1, fontSize:13 }}>{kp}</span>
                <button type="button" className="btn btn-ghost btn-xs"
                  onClick={() => upL(lesson.id, 'keyPoints', (lesson.keyPoints||[]).filter((_,i)=>i!==ki))}>✕</button>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">📋 ملخص الدرس</label>
            <textarea className="form-input" rows={3}
              placeholder="ملخص قصير يظهر للطلاب بعد المشاهدة..."
              value={lesson.summary || ''}
              onChange={e => upL(lesson.id, 'summary', e.target.value)} />
          </div>
        </div>
      )}

      {/* PDF section */}
      <div className="lesson-pdf-section">
        <button type="button" className="lesson-pdf-toggle" onClick={handleShowPdfs}>
          📎 ملفات PDF للدرس {pdfs.length > 0 && <span className="pdf-count-badge">{pdfs.length}</span>}
          <span style={{ marginRight: 'auto' }}>{showPdfs ? '▲' : '▼'}</span>
        </button>

        {showPdfs && (
          <div className="lesson-pdf-panel">
            {loadingPdfs ? (
              <div style={{ padding: '12px 0', textAlign: 'center' }}><div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /></div>
            ) : (
              <>
                {pdfs.length > 0 && (
                  <div className="pdf-list-mini">
                    {pdfs.map(p => (
                      <div key={p.id} className="pdf-mini-item">
                        <span>📄 {p.fileName}</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <a href={p.fileURL} target="_blank" rel="noreferrer" className="btn btn-xs btn-primary" download>تحميل</a>
                          <button className="btn btn-xs" style={{ color: 'var(--danger)', border: '1px solid var(--danger)', background: 'transparent' }}
                            onClick={() => handlePdfDelete(p.id, p.fileName)}>حذف</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="pdf-upload-mini">
                  <input type="text" className="form-input" placeholder="اسم الملف (اختياري)"
                    value={pdfName} onChange={e => setPdfName(e.target.value)} />
                  <label className="pdf-file-pick">
                    {pdfFile ? `📄 ${pdfFile.name}` : '📁 اختر PDF'}
                    <input type="file" accept=".pdf" onChange={e => setPdfFile(e.target.files[0])} style={{ display: 'none' }} />
                  </label>
                  <button type="button" className="btn btn-outline btn-sm" onClick={handlePdfUpload} disabled={uploading || !pdfFile}>
                    {uploading ? 'جارٍ الرفع...' : '↑ رفع PDF'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Create Course Form (Multi-Step Wizard) ─── */
function CreateCourseForm({ userDoc, onCreated }) {
  const { register, handleSubmit, reset, setValue, getValues, watch } = useForm({
    defaultValues: { isLive: 'false' }
  });
  const [step,          setStep]          = useState(1);
  const [lessons,       setLessons]       = useState([]);
  const [creating,      setCreating]      = useState(false);
  const [courseSubject, setCourseSubject] = useState('');
  const [courseGrade,   setCourseGrade]   = useState('');
  const [courseSlug,    setCourseSlug]    = useState('');
  const [slugStatus,    setSlugStatus]    = useState(null);
  const [waLink,        setWaLink]        = useState('');
  const [paymentType,   setPaymentType]   = useState('full');
  const [quizStrategy,  setQuizStrategy]  = useState('per_lesson');
  const thumbInputRef = useRef();
  const [thumbUrl, setThumbUrl]         = useState('');
  const [thumbUploading, setThumbUploading] = useState(false);

  const title = watch('title');
  const watchedSlug = watch('slug');

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (watchedSlug) {
        try {
          const { data } = await api.get(`/courses/check-slug/${watchedSlug}`);
          setSlugStatus(data);
        } catch { setSlugStatus(null); }
      } else { setSlugStatus(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [watchedSlug]);

  const handleThumb = async (file) => {
    setThumbUploading(true);
    try { const url = await uploadCourseThumbnail(file); setThumbUrl(url); toast.success('✅ تم رفع الصورة!'); }
    catch { toast.error('فشل رفع الصورة'); }
    setThumbUploading(false);
  };

  const nextStep = () => {
    if (step === 1 && (!getValues('title') || !courseSubject)) return toast.error('العنوان والمادة مطلوبان');
    if (step === 2 && lessons.length === 0) return toast.error('يجب إضافة درس واحد على الأقل');
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  const onSaveCourse = async (data) => {
    setCreating(true);
    try {
      const { data: newCourse } = await createCourse({
        title:       data.title,
        slug:        data.slug,
        description: data.description,
        subject:     courseSubject,
        grade:       courseGrade,
        price:       paymentType === 'perLesson' ? 0 : (parseFloat(data.price) || 0),
        isLive:      data.isLive === 'true',
        paymentType,
        quizStrategy,
        whatsappGroupLink: waLink,
        thumbnail:   thumbUrl,
        lessons: lessons.map(l => ({
          id:                 l.id,
          title:              l.title,
          isFree:             l.isFree,
          cloudinaryPublicId: l.cloudinaryPublicId || '',
          directVideoUrl:     l.directVideoUrl     || '',
          duration:           l.duration           || '',
          keyPoints:          l.keyPoints          || [],
          summary:            l.summary            || '',
          price:              paymentType === 'perLesson' ? (parseFloat(l.price) || 0) : 0,
        })),
      });

      for (let i = 0; i < lessons.length; i++) {
        if (lessons[i].quiz) {
          try { await createQuiz({ ...lessons[i].quiz, courseId: newCourse.id, lessonIndex: i }); }
          catch (e) { console.error('Failed to create quiz for lesson', i, e); }
        }
      }

      toast.success('✅ تم إنشاء الكورس! في انتظار موافقة الإدارة.');
      reset(); setCourseSubject(''); setCourseGrade(''); setCourseSlug(''); setLessons([]); setThumbUrl(''); setPaymentType('full'); setStep(1);
      if (onCreated) onCreated();
    } catch (err) { toast.error(err.response?.data?.error || 'حدث خطأ'); }
    setCreating(false);
  };

  return (
    <div className="create-course-wrap" style={{ maxWidth: 800 }}>
      {/* Wizard Header */}
      <div className="wizard-steps" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 30, position: 'relative' }}>
        <div style={{ position: 'absolute', top: '15px', left: '10%', right: '10%', height: '2px', background: 'var(--border)', zIndex: 1 }} />
        {[1, 2, 3].map(s => (
          <div key={s} style={{ zIndex: 2, background: step >= s ? 'var(--blue)' : 'var(--border)', color: '#fff', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, transition: '0.3s' }}>
            {s}
          </div>
        ))}
      </div>

      {!userDoc?.isApproved && step === 1 && (
        <div className="approval-notice" style={{ marginBottom: 20 }}>
          ⚠️ حسابك لم يُوافق عليه بعد. يمكنك إنشاء الكورس الآن لكن لن يُنشر حتى تتم الموافقة.
        </div>
      )}

      <form onSubmit={handleSubmit(onSaveCourse)}>
        {/* STEP 1: BASICS */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="card" style={{ padding: 24 }}>
              <h3 className="form-section-title">1️⃣ معلومات الكورس الأساسية</h3>
              <div className="form-group">
                <label className="form-label">رابط جروب واتساب (اختياري)</label>
                <input className="form-input" placeholder="https://chat.whatsapp.com/..." value={waLink} onChange={e => setWaLink(e.target.value)} dir="ltr" />
              </div>
          <div className="form-group">
            <label className="form-label">الرابط المختصر (Slug) *</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, direction: 'ltr' }}>
              <span style={{ color: 'var(--text-muted)' }}>madrasak.eg/courses/</span>
              <input
                className="form-input"
                placeholder="example-course-slug"
                {...register('slug', {
                  required: true,
                  pattern: { value: /^[a-z0-9\u0600-\u06FF-]+$/, message: 'أحرف وأرقام وشرطات فقط' }
                })}
              />
            </div>
            {slugStatus && (
              <p style={{ fontSize: 11, color: slugStatus.available ? 'var(--success)' : 'var(--danger)', marginTop: 4 }}>
                {slugStatus.available ? '✅ الرابط متاح' : '❌ الرابط مستخدم بالفعل'}
              </p>
            )}
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, textAlign: 'right' }}>هذا هو الرابط الذي سيظهر في المتصفح.</p>
          </div>
              <div className="form-group">
                <label className="form-label">عنوان الكورس *</label>
                <input
                  className="form-input"
                  placeholder="مثال: فيزياء تالتة ثانوي — الباب الأول"
                  {...register('title', { required: true })}
                  onChange={(e) => {
                    const title = e.target.value;
                    setValue('title', title);
                    const slug = title.trim()
                      .toLowerCase()
                      .replace(/[^a-z0-9\u0600-\u06FF]/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-|-$/g, '');
                    setValue('slug', slug);
                  }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">وصف الكورس</label>
                <textarea className="form-input" rows={4} placeholder="اشرح للطلاب هيتعلموا إيه في الكورس ده..." {...register('description')} />
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">المادة *</label>
                  <SubjectSelector value={courseSubject} onChange={val => { setCourseSubject(val); setValue('subject', val); }} />
                </div>
                <div className="form-group">
                  <label className="form-label">الصف الدراسي</label>
                  <select className="form-input" value={courseGrade} onChange={e => setCourseGrade(e.target.value)}>
                    <option value="">-- اختر الصف (اختياري) --</option>
                    {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">نوع المحتوى</label>
                <select className="form-input" {...register('isLive')}>
                  <option value="false">🎬 فيديوهات مسجلة</option>
                  <option value="true">🔴 حصص لايف (بث مباشر)</option>
                </select>
              </div>
              <div style={{ marginBottom: 16, padding: 12, background: 'rgba(13,212,184,0.05)', border: '1px solid var(--teal)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13 }}>
                  <span style={{ display: 'block', fontWeight: 700, color: 'var(--teal)' }}>🎁 قبول رصيد الهدايا كشراكة مع المنصة</span>
                  <span style={{ fontSize: 11, opacity: 0.8 }}>هل تسمح للطلاب باستخدام رصيدهم لشراء هذا الكورس؟</span>
                </div>
                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                  <input type="checkbox" {...register('allowCredits')} style={{ opacity: 0, width: 0, height: 0 }} />
                  <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, background: watch('allowCredits') ? 'var(--teal)' : '#ccc', transition: '0.3s', borderRadius: 24 }}>
                    <span style={{ position: 'absolute', height: 18, width: 18, left: watch('allowCredits') ? 22 : 3, bottom: 3, background: '#fff', transition: '0.3s', borderRadius: '50%' }} />
                  </span>
                </label>
              </div>
              <button type="button" className="btn btn-primary" style={{ width: '100%', marginTop: 10 }} onClick={nextStep}>المتابعة لإضافة الدروس ←</button>
            </div>
          </motion.div>
        )}

        {/* STEP 2: CONTENT */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <h3 className="form-section-title" style={{ marginBottom: 15 }}>2️⃣ دروس الكورس: {title}</h3>
            <LessonsBuilder lessons={lessons} setLessons={setLessons} paymentType={paymentType} />
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={prevStep}>السابق</button>
              <button type="button" className="btn btn-primary" style={{ flex: 2 }} onClick={nextStep}>المتابعة للتسعير والنشر ←</button>
            </div>
          </motion.div>
        )}

        {/* STEP 3: FINALIZE */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="card" style={{ padding: 24, marginBottom: 20 }}>
              <h3 className="form-section-title">3️⃣ التسعير والإعدادات النهائية</h3>

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">💰 نظام البيع</label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="button" className={`btn ${paymentType === 'full' ? 'btn-primary' : 'btn-outline'}`} style={{ flex: 1 }} onClick={() => setPaymentType('full')}>📦 الكورس كاملاً</button>
                  <button type="button" className={`btn ${paymentType === 'perLesson' ? 'btn-primary' : 'btn-outline'}`} style={{ flex: 1 }} onClick={() => setPaymentType('perLesson')}>🎟️ درس بدرس</button>
                </div>
              </div>

              {paymentType === 'full' && (
                <div className="form-group">
                  <label className="form-label">السعر الكلي (ج.م)</label>
                  <input type="number" className="form-input" min={0} placeholder="مثلاً: 150" {...register('price')} />
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>اكتب 0 لو الكورس مجاني</p>

                  {watch('price') > 0 && (
                    <div style={{ marginTop: 15, padding: 15, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                        <span>💰 سعر الكورس:</span>
                        <span>{watch('price')} ج.م</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5, color: '#ef4444' }}>
                        <span>📉 عمولة المنصة ({earnings.commission}%):</span>
                        <span>-{(watch('price') * (earnings.commission / 100)).toFixed(2)} ج.م</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: 'var(--success)', borderTop: '1px solid #bbf7d0', paddingTop: 8, marginTop: 5 }}>
                        <span>✨ صافي ربحك:</span>
                        <span>{(watch('price') * (1 - earnings.commission / 100)).toFixed(2)} ج.م</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">📝 نظام الاختبارات</label>
                <select className="form-input" value={quizStrategy} onChange={e => setQuizStrategy(e.target.value)}>
                  <option value="per_lesson">📖 اختبار بعد كل درس</option>
                  <option value="per_unit">🗂️ اختبار بعد كل وحدة</option>
                  <option value="end_of_course">🎓 اختبار نهائي في نهاية الكورس</option>
                </select>
              </div>

              <div className="form-group" style={{ marginTop: 20 }}>
                <label className="form-label">🖼️ صورة الغلاف (Thumbnail)</label>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  {thumbUrl && <img src={thumbUrl} alt="" style={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 8 }} />}
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => thumbInputRef.current?.click()} disabled={thumbUploading}>
                    {thumbUploading ? 'جاري الرفع...' : '📸 ' + (thumbUrl ? 'تغيير الصورة' : 'رفع صورة')}
                  </button>
                  <input ref={thumbInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files[0] && handleThumb(e.target.files[0])} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={prevStep}>السابق</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={creating}>
                {creating ? <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : '🚀 حفظ ونشر الكورس'}
              </button>
            </div>
          </motion.div>
        )}
      </form>
    </div>
  );
}

/* ─── Teacher Profile Form ─── */
function PlatformReviewTab() {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    api.get('/reviews/platform/my')
      .then(r => setExisting(r.data))
      .catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (comment.length < 5) return toast.error('التعليق قصير جداً');
    setLoading(true);
    try {
      const { data } = await api.post('/reviews/platform', { rating, comment });
      setExisting(data.review);
      toast.success('✅ شكرأ لتقييمك!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'فشل إرسال التقييم');
    }
    setLoading(false);
  };

  if (existing) {
    return (
      <div className="card" style={{ padding: 30, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 15 }}>🌟</div>
        <h2 style={{ marginBottom: 10 }}>شكراً على تقييمك!</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>تقييمك يساعدنا على تحسين المنصة ويوفر الثقة للمستخدمين الجدد.</p>
        <div className="card" style={{ padding: 15, background: '#f8fafc', display: 'inline-block', minWidth: 300 }}>
          <div style={{ color: '#f59e0b', fontSize: 20, marginBottom: 8 }}>{'★'.repeat(existing.rating)}{'☆'.repeat(5-existing.rating)}</div>
          <p style={{ fontStyle: 'italic' }}>"{existing.comment}"</p>
        </div>
        <div style={{ marginTop: 20 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { setComment(existing.comment); setRating(existing.rating); setExisting(null); }}>✏️ تعديل التقييم</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 24, maxWidth: 600, margin: '0 auto' }}>
      <h2 className="db-section-title">قيم تجربتك مع مدرسك ⭐</h2>
      <p style={{ marginBottom: 20, fontSize: 14, color: 'var(--text-muted)' }}>رأيك يهمنا جداً في تطوير المنصة للأفضل.</p>

      <form onSubmit={onSubmit}>
        <div className="form-group">
          <label className="form-label">التقييم</label>
          <div style={{ display: 'flex', gap: 10, fontSize: 30, cursor: 'pointer', direction: 'ltr', justifyContent: 'flex-end' }}>
            {[1, 2, 3, 4, 5].map(s => (
              <span key={s} onClick={() => setRating(s)} style={{ color: s <= rating ? '#f59e0b' : '#cbd5e1' }}>
                {s <= rating ? '★' : '☆'}
              </span>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">رأيك في المنصة</label>
          <textarea
            className="form-input"
            rows={5}
            placeholder="اكتب تجربتك هنا... ما الذي أعجبك؟ وما الذي يحتاج لتحسين؟"
            value={comment}
            onChange={e => setComment(e.target.value)}
            required
          />
        </div>

        <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
          {loading ? 'جاري الإرسال...' : 'نشر التقييم'}
        </button>
      </form>
    </div>
  );
}

function TeacherProfileForm({ userDoc, refreshUserDoc }) {
  const { register, handleSubmit, watch, formState: { isSubmitting } } = useForm({
    defaultValues: {
      name: userDoc?.name,
      username: userDoc?.username,
      bio: userDoc?.bio,
      title: userDoc?.title,
      pricePerHour: userDoc?.pricePerHour,
      phone: userDoc?.phone,
      'socialMedia.facebook': userDoc?.socialMedia?.facebook || '',
      'socialMedia.instagram': userDoc?.socialMedia?.instagram || '',
      'socialMedia.youtube': userDoc?.socialMedia?.youtube || '',
      'socialMedia.tiktok': userDoc?.socialMedia?.tiktok || '',
      'socialMedia.linkedin': userDoc?.socialMedia?.linkedin || '',
    },
  });
  const [selectedSubjects, setSelectedSubjects] = useState(userDoc?.subjects || []);
  const [subjectImages, setSubjectImages] = useState(userDoc?.subjectImages || {});
  const [uploadingSubj, setUploadingSubj] = useState(null);
  const watchedUsername = watch('username');
  const [userStatus, setUserStatus] = useState(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (watchedUsername && watchedUsername !== userDoc?.username) {
        try {
          const { data } = await api.get(`/users/check-username/${watchedUsername}`);
          setUserStatus(data);
        } catch { setUserStatus(null); }
      } else { setUserStatus(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [watchedUsername, userDoc?.username]);

  const handleSubjectImage = async (subj, file) => {
    if (!file) return;
    setUploadingSubj(subj);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('subject', subj);
      const { data } = await uploadSubjectImage(fd);
      setSubjectImages(prev => ({ ...prev, [subj]: data.url }));
      toast.success(`✅ تم رفع صورة لـ ${subj}`);
    } catch { toast.error('فشل رفع الصورة'); }
    setUploadingSubj(null);
  };

  const onSubmit = async (data) => {
    try {
      await updateMe({
        name: data.name,
        username: data.username,
        bio: data.bio,
        title: data.title,
        subjects: selectedSubjects,
        subjectImages: subjectImages,
        pricePerHour: parseFloat(data.pricePerHour) || 0,
        phone: data.phone,
        socialMedia: data.socialMedia,
      });
      await refreshUserDoc();
      toast.success('✅ تم تحديث الملف الشخصي!');
    } catch { toast.error('حدث خطأ'); }
  };

  return (
    <div className="create-course-wrap">
      <h2 className="db-section-title">الملف الشخصي</h2>
      <div className="card" style={{ padding: 24, marginBottom: 20 }}>
        <div className="profile-avatar-row">
          <AvatarUpload />
          <div><div style={{ fontWeight: 700 }}>{userDoc?.name}</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{userDoc?.email}</div></div>
        </div>
        <h3 className="form-section-title" style={{ marginTop: 20 }}>صورة الغلاف</h3>
        <BannerUpload current={userDoc?.banner} />
      </div>
      <div className="card" style={{ padding: 24 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group"><label className="form-label">الاسم *</label><input className="form-input" {...register('name', { required: true })} /></div>
          <div className="form-group">
            <label className="form-label">اسم المستخدم المميز (Username) *</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, direction: 'ltr' }}>
              <span style={{ color: 'var(--text-muted)' }}>madrasak.eg/teachers/</span>
              <input
                className="form-input"
                placeholder="username"
                {...register('username', {
                  required: true,
                  pattern: { value: /^[a-z0-9]+$/, message: 'أحرف إنجليزية وأرقام فقط' }
                })}
              />
            </div>
            {userStatus && (
              <p style={{ fontSize: 11, color: userStatus.available ? 'var(--success)' : 'var(--danger)', marginTop: 4 }}>
                {userStatus.available ? '✅ اسم المستخدم متاح' : '❌ اسم المستخدم مستخدم بالفعل'}
              </p>
            )}
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, textAlign: 'right' }}>هذا الرابط الذي ستشاركه مع طلابك. أحرف إنجليزية وأرقام فقط.</p>
          </div>
          <div className="form-group"><label className="form-label">المسمى الوظيفي</label><input className="form-input" placeholder="مثلاً: كبير معلمي اللغة العربية" {...register('title')} /></div>
          <div className="form-group"><label className="form-label">رقم الهاتف</label><input className="form-input" {...register('phone')} /></div>
          <div className="form-group"><label className="form-label">نبذة عنك</label><textarea className="form-input" rows={4} placeholder="اكتب نبذة مختصرة عنك وعن تخصصك..." {...register('bio')} /></div>
          <div className="form-group"><label className="form-label">سعر الساعة (ج.م)</label><input type="number" className="form-input" min={0} {...register('pricePerHour')} /></div>

          <h3 className="form-section-title" style={{ marginTop: 20 }}>🔗 مواقع التواصل الاجتماعي</h3>
          <div className="grid-2">
            <div className="form-group"><label className="form-label">فيسبوك</label><input className="form-input" placeholder="https://facebook.com/..." {...register('socialMedia.facebook')} /></div>
            <div className="form-group"><label className="form-label">إنستجرام</label><input className="form-input" placeholder="https://instagram.com/..." {...register('socialMedia.instagram')} /></div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label className="form-label">يوتيوب</label><input className="form-input" placeholder="https://youtube.com/..." {...register('socialMedia.youtube')} /></div>
            <div className="form-group"><label className="form-label">تيك توك</label><input className="form-input" placeholder="https://tiktok.com/..." {...register('socialMedia.tiktok')} /></div>
          </div>
          <div className="form-group"><label className="form-label">LinkedIn</label><input className="form-input" placeholder="https://linkedin.com/in/..." {...register('socialMedia.linkedin')} /></div>
          <div className="form-group">
            <label className="form-label">المواد التي تدرّسها (أضف صور مخصصة لكل مادة)</label>
            <SubjectSelector value="" onChange={val => !selectedSubjects.includes(val) && setSelectedSubjects(prev => [...prev, val])} />

            <div style={{ marginTop: 15, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
              {selectedSubjects.map(s => (
                <div key={s} className="card" style={{ padding: 10, textAlign: 'center', position: 'relative' }}>
                  <button type="button" onClick={() => setSelectedSubjects(p => p.filter(x => x !== s))} style={{ position: 'absolute', top: 5, right: 5, background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 10 }}>✕</button>

                  <div style={{ width: '100%', height: 60, background: '#f1f5f9', borderRadius: 6, marginBottom: 8, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {subjectImages[s] ? (
                      <img src={subjectImages[s]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: 20 }}>📖</span>
                    )}
                  </div>

                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5 }}>{s}</div>

                  <label className="btn btn-ghost btn-xs" style={{ cursor: 'pointer', display: 'block' }}>
                    {uploadingSubj === s ? '...' : '📸 صورة'}
                    <input type="file" hidden accept="image/*" onChange={e => e.target.files[0] && handleSubjectImage(s, e.target.files[0])} disabled={uploadingSubj === s} />
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">البريد الإلكتروني</label>
            <input className="form-input" value={userDoc?.email || ''} disabled style={{ opacity: .6 }} />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={isSubmitting}>
            {isSubmitting ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : 'حفظ التغييرات'}
          </button>
        </form>
      </div>
    </div>
  );
}
